/**
 * The `en` string catalogue — the single source for every user-visible string
 * (architecture §14 rule 10).
 *
 * Ownership: **Theo owns the contents.** Sprint 2's copy is his T-171 deck
 * (`projects/wordy-royale/design/wr-s2-copy.md`), merged here verbatim in
 * T-170; keys are the camelCase per-segment dot paths that deck specifies.
 * Strings that Ivy wrote *directly* into the design spec as literal button
 * labels ("Log in instead", "Request a new link", …) are her final call, not
 * copy slots, and are carried here unchanged so rule 10 still holds.
 *
 * Placeholders are `{name}` and are substituted by `t(key, { name })`. A key
 * whose value must carry a tappable link (`auth.tos`) is interpolated with
 * React nodes by `apps/web/src/lib/rich.tsx`, not by string concatenation.
 *
 * Every string here is final copy. The phase plan's S2-6 DoD is a literal
 * `grep` over this package for the placeholder marker Sprint 1 used; it must
 * return nothing, so the marker does not appear here even inside a comment.
 * `i18n.test.ts` enforces the same rule in CI.
 */
export const en = {
    // ---- app-wide -----------------------------------------------------------
    "app.name": "Wordy Royale",
    "app.wordy": "WORDY",
    "app.royale": "ROYALE",
    /**
     * **SUPERSEDES `"Word games, played for real stakes."`** — Theo,
     * `design/wr-s2-copy.md`, "Real Stakes → Real Fun" basket §2, ratified by
     * Aaron 2026-08-04 (*"'Real Stakes' can be replaced with 'Real Fun'"*). That
     * ruling closes the escalation the same-day KEEP ratification below carried,
     * and this key takes the identical stakes→fun substitution Aaron ratified for
     * `auth.panel.tagline`. 32 ch, down from 36.
     *
     * "Fun" needs no per-table-state check the way "stakes" and "opponents" did:
     * it is not a categorical claim, so no table configuration can make it false.
     * The meta description's "coins on the line" (Nora's §4 flag) is **out of
     * scope and unchanged** — the deck's §3 ruled the ruling does not reach it,
     * because it names the concrete, ToS-defined currency rather than asserting
     * an unnamed wager.
     *
     * Chain, so no build can pick up a stale generation: `"…played against real
     * people, for real stakes."` (pre-T-279) → `"Word games, played for real
     * stakes."` (T-279, dropping the opponent-species claim; ratified "KEEP,
     * unchanged" by Theo 2026-08-04 in the stragglers addendum §2, with "stakes"
     * escalated to Aaron/legal as gambling-register vocabulary) → this string.
     *
     * Theo's T-268 deck §5 *flagged* this key without ratifying a successor: it
     * "makes the identical claim in a different string, on a different surface …
     * It has the same defect for the same reason and on the same deploy", and he
     * asked that whoever cuts the wave's promotion ticket route it back to him.
     * T-279 does that, and applies the **identical substitution pattern** he used
     * on `auth.panel.tagline` in the meantime so the wave cannot ship the false
     * claim while the ratification is in flight: the clause that claims the
     * opponent's *species* is the only thing that moves, and the sentence keeps
     * its shape and its "real stakes" close.
     *
     * Two facts for that ratification, both established here rather than assumed:
     *
     * 1. **Nothing renders this key.** `t("app.tagline")` has no call site in
     *    `apps/web` (the login panel renders `auth.panel.tagline`; the lobby
     *    renders `lobby.type.<slug>.tagline`). It ships inside the catalogue and
     *    is read by nobody, which answers the deck's "worth Rafi confirming which
     *    screen renders it" — the answer is none.
     * 2. **The same false claim WAS rendered, from outside this catalogue**:
     *    `apps/web/index.html`'s `<meta name="description">` read "word games
     *    played against real people" when T-279 raised it to Morgan rather than
     *    editing it here. It has since been replaced by Nora's string (stragglers
     *    addendum §4) and is guarded by `meta-description.test.ts`; it is not an
     *    i18n key and stays out of this key's scope.
     */
    "app.tagline": "Word games, played for real fun.",
    // ---- splash (design spec §5) --------------------------------------------
    // The Sprint 1 splash block (`splash.heading` / `.subheading` /
    // `.enterDevRoom` / `splash.api.*`) was retired in T-170: the production
    // splash below supersedes it, and Theo flagged leaving it as dangling dead
    // copy. `/dev/room` lost its front door then and was deleted outright by A60.
    "splash.loading": "Loading Wordy Royale",
    "splash.slow": "Setting up the board — almost there.",
    "splash.error": "We can't reach Wordy Royale right now. Check your connection.",
    "splash.retry": "Try again",
    "splash.buildWeb": "web",
    "splash.buildApi": "api",
    // ---- auth: login / sign-up (§6) -----------------------------------------
    "auth.tab.login": "Log in",
    "auth.tab.signup": "Sign up",
    "auth.tabsLabel": "Log in or sign up",
    "auth.title.login": "Welcome back",
    "auth.title.signup": "Create your account",
    "auth.cta.login": "Log in",
    "auth.cta.signup": "Create account",
    "auth.cta.guest": "Continue as guest",
    "auth.email": "Email",
    "auth.password": "Password",
    "auth.passwordReveal": "Show password",
    "auth.passwordHide": "Hide password",
    "auth.forgot": "Forgot password?",
    "auth.or": "or",
    "auth.emailInvalid": "That doesn't look like an email address.",
    "auth.passwordTooShort": "Passwords are at least {min} characters.",
    // LOCKED (§6): one generic message for wrong-email, wrong-password and
    // no-such-account. Never a field-level "wrong password".
    "auth.error.credentials": "That email or password isn't right. Try again.",
    "auth.error.lockout": "Too many tries. Try again in {time}.",
    "auth.error.unverified": "Your email isn't verified yet. Resend the link below.",
    // LOCKED (§6, PRD §4.2): sign-up DOES disclose a duplicate address. This is
    // the deliberate asymmetry with `auth.error.credentials` — not a bug.
    "auth.error.emailTaken": "That email's already registered. Log in instead, or reset your password.",
    "auth.error.tosRequired": "Tick the box to agree to the Terms and Privacy Policy.",
    "auth.action.loginInstead": "Log in instead",
    "auth.action.resetPassword": "Reset password",
    "auth.tos": "I agree to the {tosLink} and {privacyLink}.",
    "auth.tos.terms": "Terms",
    "auth.tos.privacy": "Privacy Policy",
    /**
     * The age + acceptance attestation under the auth card and under the
     * conversion sheet's submit — **T-234, and it is a legal requirement rather
     * than a copy choice.** ToS §1 and Privacy §8 both set the floor at 13; the
     * product had no age attestation anywhere, and the ToS preamble's own
     * acceptance clause is a by-continuing one ("By creating an account, playing
     * as a guest, or otherwise using the Service, you agree to these Terms"),
     * which is why this is a sentence and not a second checkbox.
     *
     * The link labels are the documents' own titles (`legal.terms.title`,
     * `legal.privacy.title`), not `auth.tos.terms`'s short "Terms": an
     * attestation names the instrument it attests to.
     *
     * **Supersedes `auth.legal`** ("By continuing you agree to the … and …"),
     * retired here rather than left behind — it occupied this exact slot and
     * nothing else rendered it (the S3-5 dangling-copy rule).
     *
     * **Renamed from `auth.legal_attestation` in the S5 pre-QA fold (T-236)**, per
     * T-234 flag 2 and this catalogue's dominant camelCase-per-segment convention.
     * A rename, not a rewrite: **the sentence is ratified and unchanged** — it is
     * the wording T-234 shipped and the legal finals were checked against it
     * (Theo, T-235 §E, who confirms it makes the ToS preamble's three claims and
     * names both documents by their own titles).
     */
    "auth.legalAttestation": "By continuing you confirm you are at least 13 years old and agree to the {tosLink} and {privacyLink}.",
    /**
     * **SUPERSEDES the T-171 string** `"Real words. Real opponents. Real stakes."`
     * — Theo's T-268 deck §5, ratified 2026-08-03, and rated a Blocker on the
     * deploy that ships bots by both Ivy (§8 amendment 4) and Dex (A87 item 4).
     *
     * "Real opponents" is a **categorical** claim — every opponent you meet is a
     * person — and it goes false the moment a free-tile seat can be filled by a
     * disclosed bot. That LOCKED rule 32 keeps bots off every fee-bearing table
     * does not save it: this line sits on the login screen, before a player has
     * picked a table, and speaks for the whole product. "Games" makes no claim
     * about the opponent's species and keeps the triad's three beats.
     *
     * **"Real stakes" is now gone too — second supersession, ratified by Aaron
     * 2026-08-04** (*"'Real Stakes' can be replaced with 'Real Fun'"*), per
     * Theo's "Real Stakes → Real Fun" basket §1. Not a factual correction: Theo
     * had checked "stakes" and it held (free rooms have paid `0` since Juno's
     * Ruling #2, so the line was never a per-game guarantee that money is on the
     * table). It goes because "stakes" is an abstract wager-noun with no named
     * referent, which reads as gambling-register to a cold reader — Theo's own
     * escalation to Aaron/legal, and this is the answer. "Fun" makes no
     * categorical claim at all, so unlike "opponents" and "stakes" there is no
     * table state that can falsify it, and the triad's three beats survive a
     * third generation.
     *
     * **Do not re-ship either earlier string** — not T-171's "Real words. Real
     * opponents. Real stakes.", not T-268's "Real words. Real games. Real
     * stakes." 33 ch, budget ≤45.
     */
    "auth.panel.tagline": "Real words. Real games. Real fun.",
    // COPY-GAP (Rafi, T-170): the verification link lands back on /login?verified=1
    // (the callback the API bakes into the mail) and that arrival needs a line.
    // Neither Ivy's §17 register nor Theo's deck has a slot for it. Written to
    // his voice and flagged in the ticket for him to ratify.
    "auth.verified": "Email verified. Log in to start playing.",
    // COPY-GAP (Rafi, T-183): Ivy registered `auth.banner.just_converted` as a
    // COPY-SLOT in §17 on 2026-07-28 (§14.2 item 7); Theo writes it in this
    // sprint's copy pass and has not yet. Interim string written to his tone
    // brief, inside the ≤90-character budget. **Constraints it must keep:** say
    // the account exists AND that it is waiting on verification — the login form
    // on this screen cannot succeed until then, and §2.3's honesty rule means we
    // say so rather than let the player find out by being refused. **No resend
    // prompt** (§14.2 item 7 rule 5): §6 `error-unverified` owns that control,
    // and two controls firing the same request in one viewport is the §13.2
    // error exactly.
    "auth.banner.just_converted": "Your account's created. Verify your email, then log in here.",
    "auth.strength.length": "At least {min} characters",
    "auth.strength.case": "Upper and lower case",
    "auth.strength.number": "A number or symbol",
    "auth.strength.tooWeak": "Pick a longer password to continue.",
    // ---- verify email (§7) ---------------------------------------------------
    "verify.title": "Check your inbox",
    "verify.body": "Tap the link we sent to confirm it's you.",
    "verify.stillPending": "Still not verified. Check your inbox (and spam).",
    "verify.resend": "Resend email",
    "verify.resendWait": "Resend in",
    "verify.continue": "I've verified — continue",
    "verify.changeEmail": "Use a different email",
    "verify.resent": "Sent. Check your inbox.",
    "verify.rateLimited": "That's a lot of emails. Wait a minute and try again.",
    "verify.linkExpired": "That verification link has expired. Send yourself a new one.",
    // ---- forgot / reset password (§8) ---------------------------------------
    "forgot.title": "Forgot your password?",
    "forgot.submit": "Send reset link",
    "forgot.sent": "If that email's in our system, we've sent a reset link. Check your inbox.",
    "forgot.sentTitle": "Check your inbox",
    "forgot.resend": "Send again",
    "reset.title": "Set a new password",
    "reset.newPassword": "New password",
    "reset.confirmPassword": "Confirm password",
    "reset.submit": "Set new password",
    "reset.mismatch": "Those don't match.",
    "reset.success": "Password updated. Log in with your new one.",
    "reset.expired": "This reset link has expired. Request a new one below.",
    "reset.used": "This reset link's already been used. Request a new one below.",
    "reset.invalid": "This reset link isn't valid. Request a new one below.",
    "reset.requestNew": "Request a new link",
    // ---- welcome: nickname + avatar (§9) -------------------------------------
    "welcome.title": "You're in. Who are you?",
    "welcome.body": "Pick a nickname and an avatar to enter the lobby.",
    // COPY-GAP (Rafi, T-173): QA T-172 finding #4 — `welcome.body` instructs an
    // action the guest variant of this screen forbids (the nickname is a locked
    // plate and the gallery is replaced by the conversion card, §9 LOCKED). Ivy's
    // §17 register slots one `welcome.body`; a guest needs its own sentence.
    // Written to Theo's tone brief (the reader is 15; state what is true, then
    // what to do) and flagged in the ticket for him to ratify.
    "welcome.body.guest": "Here's the name and face we picked for you. Ready when you are.",
    "welcome.nickname": "Nickname",
    "welcome.reroll": "Get another nickname",
    "welcome.checking": "Checking…",
    "welcome.available": "Available",
    // LOCKED (§9, PRD §4.4): `taken` and `unavailable` are the same string, so a
    // player cannot use the field as a profanity oracle.
    "welcome.taken": "Taken. Try one of these:",
    "welcome.unavailable": "Taken. Try one of these:",
    "welcome.suggestions": "Suggested nicknames",
    "welcome.rules": "3–20 chars · letters, numbers, underscores",
    "welcome.ruleLength": "Nicknames are 3–20 characters.",
    "welcome.ruleCharset": "Letters, numbers and underscores only.",
    "welcome.rerollFailed": "Couldn't get a new name. Try again in a sec.",
    "welcome.chooseLook": "Choose your look",
    /**
     * **Unused since §22.13 (T-265), and left rather than deleted.** It was the
     * picker's `aria-label`; the radiogroup is now named by the *visible* eyebrow
     * (`welcome.chooseLook`) through `aria-labelledby`, because Ivy's rule 1 bars
     * a name that duplicates visible text and can drift from it. Deleting a
     * player-visible string is Theo's call, not a build one — flagged in T-265's
     * output as a dangling key, the same way the splash's superseded line was.
     */
    "welcome.avatars": "Avatars",
    "welcome.continue": "Continue",
    "welcome.logout": "Not you? Log out",
    "welcome.locked": "Guests keep the nickname they were given",
    "welcome.saveFailed": "Couldn't save that. Try again.",
    "welcome.guest.convertTitle": "Want to keep this look?",
    "welcome.guest.convertBody": "Create a free account to lock in your nickname and avatar for good.",
    "welcome.guest.convertCta": "Create a free account",
    // ---- drawer nav (§11) ----------------------------------------------------
    // The RoleChip's two labels (§4: GUEST / ADMIN only). Ivy wrote them as
    // literals in the spec, so they are hers rather than a copy slot; they live
    // here because rule 10 admits no exceptions. Upper case is a `mono-sm`
    // treatment, not part of the string (§3.2).
    "role.guest": "Guest",
    "role.admin": "Admin",
    "nav.menu": "Menu",
    "nav.close": "Close menu",
    "nav.lobby": "Lobby",
    "nav.shop": "Shop",
    "nav.profile": "Profile",
    "nav.settings": "Settings",
    "nav.logout": "Log out",
    "nav.soon": "Soon",
    /**
     * **The wordmark's way home** — Ivy lobby §23.3, deck batch 2,
     * Nora-approved 2026-08-21 and shipped **verbatim**.
     *
     * One authored name for **both** DOM instances of one control: the ≥1024
     * top-bar `BrandLockup` link and the <1024 24 px mascot link. Both are
     * icon-only, so this string is the entire spoken content of each — there is
     * no visible label to concatenate with and, LOCKED, no `aria-describedby`
     * sibling (unlike the coin chip's §9.2 addendum-b split, where the
     * destination is *not* the name).
     *
     * **"Lobby", not "Home".** `nav.lobby` already calls the drawer row that
     * reaches the same place, and `lobby.headline` calls the screen itself. One
     * word for one destination; a second name for it is the ambiguity §23.3's
     * "not a §13.2 duplicate" note is careful to distinguish from — two
     * *controls* to one place is fine, two *words* for it is not.
     *
     * **"Go to", not "Return to".** Nora's own case-enumeration: a freshly
     * converted guest, or a player who arrived by a direct link, has not
     * returned from anywhere. "Go to" makes no claim about how they got here and
     * is therefore true in every state this control renders in — and it renders
     * in every state except the lobby, where it is inert.
     */
    "nav.home.a11y": "Go to the lobby",
    /**
     * ---- the top-bar coin chip's accessible name and description ------------
     *
     * **Ivy, S3 lobby spec §9.2 ADDENDUM 2026-08-18 (b) — FINAL, Nora-passed
     * (route A). Verbatim; the wording in the addendum above (b) is superseded.**
     *
     * They exist because T-440 made the `live` chip a `<button>`, and a button is
     * a name-from-content role: its `aria-label` **replaces** the subtree, so the
     * pill announced *"Coin balance, button"* — neither the number nor the
     * destination. The split is §13 rule 2's shape: the **name** says what it is,
     * the **description** says what pressing it does.
     *
     *  - `coin.a11y.balance` is the `aria-label`, on the **`live` branch only**.
     *    `live-inert` is a `<span role="status">` whose subtree text is already
     *    readable, and naming it with the number would say it twice.
     *  - `{n}` receives the **grouped, unabbreviated** value (`toLocaleString`),
     *    never `formatCoins`'s `k` form: the abbreviation exists for a pixel
     *    budget the ear does not have, so `12.4k` is spoken "12,400".
     *  - `coin.a11y.action` is carried by `aria-describedby` to a
     *    `wr-visually-hidden` **sibling after** the button — inside a named button
     *    it is dead text, and inside the live region it is noise a credit could
     *    re-announce.
     *
     * **`coin.a11y.balance.one` is NOT minted, and must not be** (Ivy, LOCKED):
     * *"A ticket or QA pass that finds a `.one` sibling for this key has found a
     * defect, not a fix."* The value carries no noun to inflect — that is the
     * whole reason route A was taken, and the sibling would re-introduce the
     * "1 coins" hazard from the other direction.
     *
     * The other two shop entrances deliberately do **not** match these strings:
     * `nav.shop` and `lobby.locked.getCoins` are name-from-content and their
     * visible words already say where they go. The chip is the only shop entrance
     * with no visible destination text.
     */
    "coin.a11y.balance": "Coin balance, {n}",
    "coin.a11y.action": "Opens the shop",
    // ---- profile flyout (§12) ------------------------------------------------
    "flyout.open": "Your profile",
    /**
     * **The desktop profile pill's accessible name — S14 `A5`, Ivy R-K3's
     * branch.** COPY PENDING NORA/THEO: this string was composed by the builder
     * on the shape of its own neighbour `coin.a11y.balance` (*"Coin balance,
     * {n}"* — affordance word, comma, value) because R-K3's check **failed** and
     * the ruling required the fix to move here. It is flagged in T-582's Output
     * for the content gate; **if the wording is re-cut, only this value changes,
     * not the mechanism.**
     *
     * **Why it exists at all.** The pill (`.wr-profile-pill`) renders the
     * nickname as visible text, so §14.1 ruling B item 3 binds it: *"the full
     * nickname is never lost."* The pill's button previously carried
     * `aria-label={t("flyout.open")}` — *"Your profile"* — which **overrides its
     * whole subtree**, so the inner span's `aria-label` was unreachable and the
     * promise was **unmet on that surface**. R-K4 makes the promise a
     * surface-level one satisfied **once**, and R-K3 puts the carrier on the
     * button. This key is that carrier.
     *
     * **The mobile avatar button deliberately keeps `flyout.open`** — it renders
     * no nickname, so ruling B item 3 does not bind it and a name it never shows
     * would be invented content.
     */
    "flyout.openNamed": "Your profile, {nickname}",
    "flyout.balance": "Coin balance",
    "flyout.changeNickname": "Change nickname",
    "flyout.changeAvatar": "Change avatar",
    "flyout.viewProfile": "View full profile",
    "flyout.offline": "Needs a connection",
    "flyout.guest.convert": "Create a free account to keep your nickname, avatar and coins for good.",
    "flyout.guest.convertCta": "Create a free account",
    // ---- guest log-out dialog (§11) -----------------------------------------
    "logout.guest.title": "Log out of this guest?",
    "logout.guest.warning": "Logging out deletes this guest account — your nickname, coins and stats go with it. Create a free account first if you want to keep them.",
    "logout.guest.create": "Create an account",
    "logout.guest.confirm": "Log out anyway",
    // ---- guest → registered conversion (§14) --------------------------------
    // COPY-GAP (Rafi, T-170): §14 names COPY-SLOTs `convert.title` and
    // `convert.benefits.*`, but they are absent from Ivy's §17 register, so
    // Theo's deck does not carry them. Written to his voice and the §14
    // constraint ("must state that coins, nickname and stats are kept"), and
    // flagged in the ticket for him to ratify.
    "convert.title": "Keep everything you've got",
    "convert.benefit.identity": "Your nickname and avatar stay yours",
    "convert.benefit.coins": "Your coins and stats carry over",
    "convert.benefit.devices": "Log in from any device",
    "convert.cta": "Create account",
    "convert.emailTaken": "That email already has an account. Logging into it leaves this guest — and its coins, nickname and stats — behind.",
    "convert.loginInstead": "Log in to that account instead",
    // The `success` handoff (§14.1 ruling A, Theo's deck §Conversion success).
    // `convert.success.body` is LOCKED on saying three things in this order:
    // the data moved → you are signed out → verify, log in, and you come back.
    // `verify.resend` is reused for the countdown; the T-170 interim
    // `convert.verifyTitle` / `.verifyBody` / `.done` were retired with the
    // panel they belonged to rather than left as dangling copy.
    "convert.success.title": "Account created",
    "convert.success.body": "Your account's ready — your nickname, coins and stats have moved over. You've been signed out for now. Verify your email, log in, and you'll land right back where you left off.",
    "convert.success.cta": "Log in",
    /**
     * **KEY EXCEPTION — `in_game` is snake_case on purpose. Do NOT rename it.**
     *
     * Every other key in this catalogue uses camelCase per-segment dot paths
     * (see the header). This one does not, and it is not a typo:
     * `convert.blocked.in_game` is the identifier as it is written in **Ivy's
     * design spec §17 COPY-SLOT register** and in **Theo's copy deck**
     * (`projects/wordy-royale/design/wr-s2-design-spec.md` §17;
     * `wr-s2-copy.md`). It was carried across verbatim in T-174 — copying a
     * canonical key exactly beats matching a house style, because the register
     * is the thing two other people search by name.
     *
     * Recorded here at S3-6 (T-183, phase-plan item 6) because T-174 logged it
     * as a deviation in a ticket, and a deviation that only lives in a ticket
     * reads as a typo to the next person who opens this file.
     */
    "convert.blocked.in_game": "You can create your account once this game's over.",
    // ---- connection banner (§13.1) — transport only, never seats -------------
    "net.reconnecting": "Reconnecting",
    "net.offline": "Can't reach the server right now.",
    "net.noInternet": "No internet connection.",
    "net.back": "Back online.",
    "net.retry": "Retry",
    // ---- connection state (dev room indicator) -------------------------------
    "connection.idle": "Not connected",
    "connection.connecting": "Connecting…",
    "connection.online": "Connected",
    "connection.reconnecting": "Reconnecting…",
    "connection.offline": "Disconnected",
    "connection.label": "Connection: {state}",
    // ---- realtime auth (S2-4) -----------------------------------------------
    // Shown when the room tier refuses a socket. The distinction matters: an
    // expired token is a shrug and a retry, a suspension is not.
    "room.auth.expired": "Your session expired. Try joining again.",
    "room.auth.suspended": "This account can't join games. Contact support if you think that's wrong.",
    "room.auth.unknownPlayer": "This account is no longer available.",
    "room.auth.unavailable": "Couldn't check your session just now. Try again in a moment.",
    "room.auth.signedOut": "Log in or continue as a guest before joining a room.",
    // ---- seat-released panel (§13.2 ruling) ---------------------------------
    // Replaces the two placeholder strings T-162 shipped. LOCKED: this copy must NOT mention the
    // connection — by the time the panel renders the socket is back, and the
    // only thing that failed was the seat.
    "room.seatLost.title": "Seat released",
    "room.seatLost.body": "You stepped away and we released it to keep the round moving. Rejoin to jump back in.",
    "room.seatLost.disconnected": "Waiting for the connection to come back.",
    // ---- dev room -----------------------------------------------------------
    // The 18 `devRoom.*` strings were deleted with the screen by §16f **A60**.
    // Nothing product-facing ever rendered them; the one place a developer-surface
    // string had leaked into product copy was the seat-released panel's label,
    // which already has its own key (`room.cta.joinAgain`, below).
    // ---- transactional email (rendered by apps/api, not the SPA) ------------
    // Theo's T-171 §Email addendum. Register shift on purpose: this lands in an
    // inbox next to a bank statement, not inside the game's chrome.
    //
    // His proposed `email.verify.cta` / `email.reset.cta` keys are deliberately
    // NOT here: `apps/api/src/mail/templates.ts` sends plain text with no HTML
    // button, so a button label would be an unused key and "if the button doesn't
    // work" would reference something that does not exist. The fallback lines
    // below are cut back to the plain-link phrasing his note specifies for that
    // case. Flagged back to Theo in the T-170 output.
    //
    // ── T-198 (Theo), ratified — applied in T-200 (QA T-195 N3) ───────────────
    //
    // Two defects fixed, both about a mail telling the reader something untrue:
    //
    //  1. `{hours} hour(s)` rendered literally as "1 hour(s)". Theo hard-coded
    //     "1 hour" rather than keeping the token, because one hour is the only
    //     value either template has ever rendered. **His flag, carried here:** if
    //     `TOKEN_TTL_SECONDS` ever stops being 3600 these two sentences must
    //     change with it — `mail.test.ts` asserts both facts in one test so that
    //     cannot be missed.
    //  2. **The verification link is NOT single-use.** It is a stateless JWT, so
    //     a consumed link clicked again inside the hour still verifies (QA proved
    //     it). Only the reset link is genuinely single-use, so only
    //     `email.reset.body` keeps that claim.
    //
    // **Adapted, and flagged back to Theo in T-200:** his ratified text says "Tap
    // the button below" and "If the button doesn't work, copy and paste this
    // link". These mails are plain text with no HTML part and no button (the
    // paragraph above), so both lines would describe an element that does not
    // exist — the same false-claim class N3 is about. The plain-link phrasing
    // already agreed for that case is kept and his substantive changes — the
    // expiry sentence and the dropped single-use claim — are applied verbatim.
    // If we ever ship an HTML part, his button wording lands with it.
    //
    // ── T-306 (Aaron, 2026-08-06): the verification mail gains an HTML part ─────
    //
    // Aaron asked for a "click HERE to confirm" link instead of a bare URL in the
    // body, so the verification mail is now multipart: an HTML part whose only
    // actionable element is an anchor, and the unchanged plain-text part beneath
    // it. A `text/plain` body cannot carry an anchor, so the raw URL stays there —
    // that is the accessible fallback, and it is what every client that refuses
    // HTML renders.
    //
    // Consequences for this catalogue, in the order someone will trip over them:
    //
    //  - **`email.verify.body` is gone; the four `email.verify.*` sentence keys
    //    below replace it.** Both parts are assembled from the same four strings
    //    by `apps/api/src/mail/templates.ts`, so the HTML and the text cannot
    //    drift into saying different things. The `\n\n` joins are layout and
    //    belong to the renderer, not to the copy. `mail.test.ts` pins the
    //    assembled plain text against the exact literal that used to live here —
    //    the text part of this mail did not change by one byte in T-306.
    //  - **`email.verify.cta` finally exists**, and it is the whole player-visible
    //    delta of that ticket. Theo's original conditional cta key is honoured;
    //    his *wording* ("Tap the button below") is still not, because this is a
    //    styled anchor and not a button, and the sentence introducing it does not
    //    name the element at all. `mail.test.ts` keeps the no-"button" assertion
    //    over both parts.
    //  - His "if the button doesn't work, copy and paste this link" fallback is
    //    deliberately still absent: it would put the raw URL back into the HTML,
    //    which is the exact thing T-306 removes. The text part is the fallback.
    //  - **`email.reset.*` is untouched** and keeps its single whole-body string.
    //    The reset mail is still plain text only and was explicitly out of scope;
    //    the shape asymmetry between the two mails is that scope line, not drift.
    "email.verify.subject": "Verify your Wordy Royale email",
    "email.verify.intro": "Welcome to Wordy Royale.",
    "email.verify.lead": "Confirm this email address to finish setting up your account:",
    "email.verify.cta": "Confirm your email",
    "email.verify.outro": "This link is valid for 1 hour. If you didn't create a Wordy Royale account, you can ignore this email.",
    "email.reset.subject": "Reset your Wordy Royale password",
    "email.reset.body": "We received a request to reset the password for the Wordy Royale account associated with this email address.\n\nSet a new password here:\n\n{link}\n\nThis link works once and expires in 1 hour. If you didn't request this, nothing has changed and you can ignore this email.",
    // ---- legal documents -----------------------------------------------------
    /**
     * **The documents themselves are not in this catalogue and must not be.**
     * `/terms` and `/privacy` render `apps/web/src/content/legal/*.md` verbatim —
     * byte-for-byte copies of the reviewed finals whose canonical origin is
     * `techdad-company/projects/wordy-royale/legal/`. Architecture §14 rule 10 is
     * about product copy; a signed-off legal instrument is the one text that must
     * never be trimmed to a character budget or paraphrased by a translator.
     *
     * These two keys are the documents' **titles as the product refers to them** —
     * the drawer's two links and the attestation line's two link labels. They are
     * the same words the documents' own `#` headings end with, on purpose: a
     * player must be able to see that the link and the page are the same thing.
     *
     * `legal.pending` ("This document isn't published yet…") was retired at T-234
     * with the stub screen it belonged to.
     */
    "legal.terms.title": "Terms of Service",
    "legal.privacy.title": "Privacy Policy",
    // ---- deferred screen bodies ---------------------------------------------
    /**
     * `settings` is still a stub. **`profile` is not, from S6-2b** — the screen
     * exists (`routes/ProfileScreen.tsx`), so its stub body `profile.body`
     * ("Your stats and history show up here once you've played a game.") was
     * **retired with the panel it belonged to**, exactly as `lobby.title` /
     * `lobby.body` went at S3-5 and `legal.pending` at T-234. Dangling copy for a
     * screen that no longer renders it is what those two retirements were about.
     *
     * `profile.title` **stays**: the top bar's centre slot renders it as a `<p>`
     * on this route (S2 §10 amendment 1), and it is the drawer's `nav.profile`
     * destination title.
     */
    "settings.title": "Settings",
    "settings.body": "Nothing to change yet. Sound, notifications and account controls land here.",
    "profile.title": "Profile",
    /* ------------------------------------------------------------------------
       The profile screen (S6-2b, T-260) — Ivy `wr-s6-profile-spec.md` §14
       ------------------------------------------------------------------------
     * **RATIFIED — Theo, 2026-08-02** (`wr-s2-copy.md` §S6 Profile screen — copy
     * ratification, applied by T-265). Every key below started as one of Ivy's
     * §14 COPY-SLOTs written to her purpose and budget but with no ratified
     * string; Theo's addendum audited all 40 strings and absences on this screen
     * and returned **37 KEEP verbatim, 1 CHANGE** (`lockedHint`, split into three
     * keys by `blockedReason` — see below) **and 2 NEW** (the `.one` plural
     * siblings). The provisional flag is lifted: these are the content owner's
     * words now, and changing one is a copy ticket, not a build decision.
     *
     * Four decisions inside that, all ratified as shipped, recorded because they
     * are not merely wording:
     *
     * 1. **`profile.identity.cancel` is NOT minted.** `common.cancel` is already
     *    "Cancel" and §16's one-string-per-fact rule outranks a register entry
     *    that would create a second one. The editor's Cancel resolves
     *    `common.cancel`.
     * 2. **`profile.account.signOut` says "Log out", not "Sign out".** Ivy's §3.1
     *    mock draws "Sign out", but §7.1 also requires *the same control as the
     *    drawer's Log out — extract it, do not fork it*, and the drawer's ratified
     *    word is "Log out". Two words for one irreversible act on two surfaces is
     *    the drift that rule exists to stop. It keeps its own key so Theo can
     *    overrule the word without touching the drawer's.
     * 3. **The amount's accessible name is TWO keys, not one `{sign}` param.**
     *    §14 registers `profile.tx.amount.a11y` as `{sign} {n} coins`, but S2
     *    §22.8.2 is binding that an accessible name is selected by shape — *"a
     *    pure `if`… no concatenation, no interpolation of one resolved string into
     *    another"* — and a `{sign}` param is exactly that interpolation. So:
     *    `.plus` / `.minus`, chosen by an `if` on the sign.
     * 4. **`profile.coins.title` and `profile.section.error` are additions.**
     *    §3.1's mock draws a "Coins" card heading and §8 specifies an
     *    `InlineMessage error` + Retry inside the stats and coins cards; neither
     *    string is in the §14 register. Flagged to Ivy as two small gaps rather
     *    than left un-rendered. `common.retry` supplies the button.
     */
    "profile.identity.edit": "Edit",
    "profile.identity.save": "Save",
    "profile.identity.saved": "Saved. That's you now.",
    "profile.identity.error": "Couldn't save that. Nothing has changed — try again.",
    "profile.identity.conflict": "Someone took that nickname while you were typing. Pick another.",
    /** Must not imply a penalty: it is a wait, not a punishment (Ivy §14). */
    "profile.identity.blocked.in_game": "You can change these once this game's over.",
    /** `{date}` is an **absolute** date, never a countdown (Ivy §4.5). Unused at v1. */
    "profile.identity.blocked.cooldown": "You can change your nickname again on {date}.",
    "profile.identity.blocked.guest": "Create a free account to change your nickname and avatar.",
    /**
     * **One string per `blockedReason` — Theo's S6 ratification addendum, N2
     * (2026-08-02), and the reason it is three keys and not one.**
     *
     * The flat key rendered for *every* reason: a registered player under a
     * cooldown read "Guest nickname, can't be changed" on the padlock while the
     * paragraph below it correctly named their return date. Two contradictory
     * facts on one card. `lockLabelFor()` in `IdentityCard.tsx` dispatches these
     * the same way `blockedNote()` already dispatches the notes underneath them
     * (Ivy §4.5 / §12 rule 3: one COPY-SLOT per reason, and the client never
     * synthesises a fourth).
     *
     *  - `.guest` is the old flat key's content, unchanged — a mechanical rename.
     *  - `.in_game` reuses "this game's over" verbatim from the note beside it.
     *  - `.cooldown` deliberately does **not** repeat the date: the absolute date
     *    renders one line below and a screen reader hits both in sequence.
     *
     * **This dispatch is the precondition on ever raising
     * `timing.nickname_cooldown_days` above 0** (Theo's addendum, T-262 N2).
     */
    "profile.identity.lockedHint.guest": "Guest nickname, can't be changed",
    "profile.identity.lockedHint.in_game": "Nickname locked until this game's over",
    "profile.identity.lockedHint.cooldown": "Nickname locked for now",
    "profile.stats.empty": "No games yet. Play one and your record starts here.",
    "profile.stats.emptyCta": "Find a table",
    "profile.coins.title": "Coins",
    "profile.coins.balance": "Balance",
    "profile.coins.empty": "Nothing has moved in or out yet.",
    "profile.coins.more": "Show more",
    /**
     * The seven `coin_transaction_reason` labels. Ivy §14's note: they are read
     * in a list, side by side, so they are **parallel** — one grammatical shape
     * across all seven, each a bare noun — and none of them restates the sign
     * ("Entry", not "Entry fee paid"). The context suffix ("· Word Search 1v1")
     * is appended by the layout, never by the string.
     *
     * `grant.conversion` is the one doing extra work: Ivy §17 item 3 notes it is
     * a converted guest's first-ever row and therefore often the first line they
     * read in the list.
     */
    "profile.tx.reason.room.entry": "Entry",
    "profile.tx.reason.room.refund": "Refund",
    "profile.tx.reason.room.prize": "Prize",
    "profile.tx.reason.shop.purchase": "Shop",
    "profile.tx.reason.admin.grant": "Grant",
    "profile.tx.reason.purchase.topup": "Top-up",
    "profile.tx.reason.grant.conversion": "Welcome bonus",
    /**
     * **The eighth label — DR-3 (T-309), A103 acceptance criterion 15.**
     * Theo's daily-reward deck, **Nora-reviewed 2026-08-06: SHIPS as written.**
     *
     * Two decisions in this one string, both from the deck, both worth keeping
     * legible:
     *
     *  - **"bonus", not "coins".** None of the seven siblings says "coins" — the
     *    amount column already carries the currency, and the reason label's only
     *    job is the reason. The draft `Daily coins` would have been the one label
     *    in eight that broke the family.
     *  - **`grant.conversion` is its sibling by kind**, so it takes that label's
     *    exact "[occasion] + bonus" shape and reads as its recurring counterpart
     *    rather than a new pattern.
     *
     * The key segment is `grant.daily` — dot-segmented, tracking the
     * `coin_transactions.reason` value verbatim (the deck corrected the spec's
     * `grant_daily`). `reasonKey()` in `CoinsCard.tsx` builds it from the reason
     * code, so a mismatch renders the raw key to a player rather than failing.
     *
     * **Nora's QUERY 2, recorded rather than resolved here:** "Welcome bonus" and
     * "Daily bonus" side by side lean on iGaming vocabulary on a product that is
     * scrubbing "rake" for the same reason. She explicitly did not block — the
     * sibling already shipped and the surface is a private history screen — and
     * routed it to Juno/Theo for the pass that owns the burn rename. **If it
     * changes, both labels change together or neither does.**
     */
    "profile.tx.reason.grant.daily": "Daily bonus",
    /**
     * Ivy §10 rule 4: *"`+45` is read as '45' by several screen readers, which
     * inverts the meaning of half the list."* `{n}` is the **absolute** value; the
     * key carries the sign as a word.
     */
    "profile.tx.amount.plus.a11y": "plus {n} coins",
    "profile.tx.amount.minus.a11y": "minus {n} coins",
    /**
     * **"plus 1 coins" — the pluralization fix, Theo's addendum (T-262's
     * accepted-deviation), using the mechanism this catalogue already has.**
     *
     * `@wr/i18n`'s header defers real ICU pluralization to the second locale;
     * until then the established fix for an English singular/plural mismatch is a
     * `.one` sibling the caller selects at `n === 1` — `lobby.subtitle.live` /
     * `lobby.subtitle.live.one` (T-189/T-192), the same shape of problem. Not a
     * `{count}` param and not a plural rule inside `t()`.
     *
     * They carry no `{n}`: at `n === 1` the number is fixed, so spelling it reads
     * cleaner than templating a value that never varies in that branch — the same
     * call `lobby.subtitle.live.one` makes with "One".
     */
    "profile.tx.amount.plus.a11y.one": "plus 1 coin",
    "profile.tx.amount.minus.a11y.one": "minus 1 coin",
    "profile.account.title": "Account",
    "profile.account.email": "Email",
    "profile.account.emailA11y": "Email address, partly hidden",
    "profile.account.signOut": "Log out",
    "profile.account.delete": "Delete account",
    /** Ivy §8's per-card failure, inside the card that failed. */
    "profile.section.error": "Couldn't load this. Try again.",
    /* ------------------------------------------------------------------------
       The stat block's labels (S6-2a, T-259)
       ------------------------------------------------------------------------
     * **These are keys the *server* selects and the client resolves.** A75: every
     * stat label is a `packages/i18n` key carried on the payload, so the array the
     * API builds and the strings the screen renders cannot drift into two orders.
     * The client never picks a label from a stat's name.
     *
     * The middle segment of the two `word_search.*` keys is the literal
     * `game_types.engine_key`, so it stays snake_case for exactly the reason
     * `lobby.type.word_search.tagline` and `convert.blocked.in_game` do.
     *
     * **A70 — the label is per engine, the column is not.** `player_stats`
     * carries generic `score_total` / `best_game_score`; Word Search's are Sana
     * §2.3's *"words found"* and §2.4's personal best. An engine with no entry in
     * `ENGINE_STAT_LABELS` falls back to the two generic keys below rather than
     * rendering a blank (A54's partial-registry posture).
     *
     * **RATIFIED — Theo, 2026-08-02**, KEEP verbatim, all nine (his S6 addendum
     * pulled them in beside the profile block: they render as screen labels on
     * the same surface). They are Sana's stat names as she wrote them (§2.1–§2.7)
     * and were not part of Ivy's §14 COPY-SLOT register; they have their content
     * owner's sign-off now. Changing one is an edit to this file and touches no
     * contract.
     */
    "stats.label.games_played": "Games played",
    "stats.label.wins": "Wins",
    "stats.label.score_total": "Points",
    "stats.label.best_game_score": "Best game",
    "stats.label.word_search.score_total": "Words found",
    "stats.label.word_search.best_game_score": "Best game",
    "stats.label.longest_win_streak": "Longest streak",
    "stats.label.current_win_streak": "Current streak",
    "stats.label.win_rate": "Win rate",
    /**
     * **Juno Ruling #9 §4, BINDING:** *"wherever the win rate is displayed, its
     * denominator is named. The percentage may not appear as a bare number
     * labelled only 'win rate'."* Without it a real profile reads "games played
     * 30 · wins 13 · win rate 52%" and the player's own arithmetic disagrees with
     * ours by nine points, with nothing on screen to explain it.
     *
     * The server ships this note **with** every rendered rate. Below Sana §2.7's
     * 10-decided-game gate it now ships **nothing at all** — see the retirement
     * note below.
     *
     * **RATIFIED and RENAMED, Theo 2026-08-02 (T-260).** His addendum takes the
     * denominator note verbatim — *"Ties aren't counted."* — over my T-259
     * provisional line, on the grounds that it names the excluded thing directly
     * and reuses the same word `room.results.tied` puts on screen during the game,
     * so a player builds one vocabulary for the concept instead of two. He also
     * renamed `winrate` → `winRate` ("every other multi-word leaf segment in this
     * catalogue is camelCase"). **Ratified-as-built, not provisional.**
     *
     * ── RETIRED HERE: `profile.stats.winRate.pending` (T-271, 2026-08-03) ──────
     *
     * *"A few games isn't enough to tell yet."* — retired, not left dangling, and
     * **superseded rather than deleted**: a copy job was cancelled by the
     * constraint that removed it, and that is worth a record.
     *
     * **Sana §8.5 C2, BINDING, amending her own §2.7:** *"Below 10 decided games
     * in a type, the win-rate slot renders nothing at all. It appears on the 10th
     * decided game, exactly as the streak chip appears on the 2nd."* A84's
     * practice game (a settled game with a bot seat) moves `games_played` but
     * neither `wins` nor `losses`, so a bot-heavy player can hold **40 games
     * played and 2 decided** — at which point this string is not merely vague, it
     * is false: they have played plenty. *"Rather than write a second string for
     * a second reason, delete the surface."* It also removes the only place the
     * profile block would have been tempted to explain its own denominator, which
     * Ivy §6.3 LOCKS it out of anyway.
     *
     * **Theo:** Sana §8.8 cancels the §7 sub-gate copy job outright — the string
     * is not owed, re-drafted or parked. Your live copy job on this surface is
     * `room.bots.stats_note` (C3's shape: positive clause first, never the bare
     * word "count", never a named class of game), which is BOT-4's, not this
     * ticket's.
     *
     * Reinstating this key requires citing §8.5 C2. The contract keys that
     * carried it (`STAT_KEYS.winRatePending`, `STAT_NOTE_KEYS.winRatePending`)
     * were retired in the same change, so nothing can emit it.
     */
    "profile.stats.winRate.note": "Ties aren't counted.",
    /* ========================================================================
       The lobby (S3-5)
       ========================================================================
     * Theo's T-187 deck, §S3 Lobby, merged verbatim in T-189. Keys are his
     * proposed column, which translates Ivy's §15 register 1:1 into this deck's
     * camelCase-per-segment convention — with his one deliberate exception:
     * `lobby.type.word_search.tagline`'s middle segment is the literal
     * `game_types.slug` the client looks it up by, so it stays snake_case for the
     * same reason `convert.blocked.in_game` did.
     *
     * The S2 stub pair `lobby.title` / `lobby.body` ("Rooms open up in the next
     * sprint") was **retired here**, not left behind: the screen it belonged to
     * no longer exists, and dangling copy is what T-170 flagged when it retired
     * the Sprint 1 splash block.
     *
     * **Not copy slots — data.** `game_types.name`, `game_modes.name` /
     * `sub_label` and `game_types.mono_label` come from the database and render
     * as-is (§15). They are not wrapped in `t()`, and a DB-sourced string in the
     * DOM is not a hardcoded-literal finding.
     */
    // ---- hero / greeting / the live line (§8, §4.1) --------------------------
    "lobby.headline": "Pick a table.",
    "lobby.greeting.morning": "Morning, {nickname}.",
    "lobby.greeting.afternoon": "Afternoon, {nickname}.",
    "lobby.greeting.evening": "Evening, {nickname}.",
    /**
     * **LOCKED (§8.3): rendered only at `n ≥ 1`.** `n` counts tiles that are
     * `filling` AND have at least one player — a table with nobody at it is not
     * "filling up", and that definition is what makes the sentence true.
     */
    "lobby.subtitle.live": "{n} tables filling up right now.",
    /**
     * COPY-GAP (Rafi, T-189) — **Theo's own flag #1, implemented rather than
     * papered over.**
     *
     * `lobby.subtitle.live` is ungrammatical at `n === 1` ("1 tables"), and
     * `n === 1` is a value §8.3's honesty rule explicitly allows. He declined to
     * reword around the grammar and named the two mechanisms: an ICU plural rule
     * in `t()`, or a second key the caller picks at one. **The second key is what
     * ships.** Pluralisation in `t()` is a locale-shaped feature — one English
     * `.one` / other split is not the rule set Slavic or Arabic needs, and
     * building a half-plural today is how a catalogue ends up with a plural
     * system nobody can extend. `@wr/i18n`'s header already says pluralisation
     * arrives with the second locale; this key is the one call site that needs it
     * until then, and it disappears into the real rule when that lands.
     *
     * Written to Theo's tone brief and inside the ≤44-character budget (30 ch);
     * the contraction matches `lobby.subtitle.live`'s register. **Flagged for
     * Theo to ratify.**
     */
    "lobby.subtitle.live.one": "One table's filling up right now.",
    /**
     * COPY-GAP (Rafi, T-192, from T-191 N1) — **flagged for Theo to ratify.**
     *
     * The shipped line was "Every table's open right now." §8.3 rule 2 asks this
     * slot for "a neutral orientation line that states what the screen is, claims
     * **no** activity, and does not apologise". That line claimed activity — the
     * strongest claim available, about every table at once — and it renders in
     * precisely the two states where it is least true: directly above the §11.2
     * empty panel ("No tables open right now") when every config is inactive, and
     * above a CLOSED tile after an `is_active` flip. One viewport, two sentences,
     * contradicting each other.
     *
     * The replacement makes no claim about any table, so it stays true across all
     * three states this slot is reachable in — every table open, every table
     * closed, none configured at all. It states what the screen is and stops.
     * 23 characters against a ≤52 budget; the honesty rule still self-resolves at
     * `n ≥ 1`, where the live line takes over and nothing here changes.
     */
    "lobby.subtitle.idle": "Every game starts here.",
    // ---- type header / tagline (§8.2, §4.2) ---------------------------------
    "lobby.type.openCount": "{n} open",
    "lobby.type.word_search.tagline": "Race the grid. Find every word first.",
    /**
     * **Word Reveal's TypeBanner tagline** (S14 `H4`), deck-verbatim and Nora-PASS.
     * 40 characters against §7's ≤48 budget, and the **same two-clause grammar**
     * as its sibling above — verb + the + noun, then the goal with "first" — so
     * the two banners read as one voice.
     *
     * The middle segment is the literal `game_types.slug` the client looks the key
     * up by, which is `hangman` (R-D2's internal identifier). **An i18n key is not
     * a rendered string**, so the slug appearing here is not an R-D1 exposure; the
     * rendered value names the game "Word Reveal" or nothing at all.
     */
    "lobby.type.hangman.tagline": "Call the letters. Reveal the word first.",
    // ---- the tile: money plate and states (§5.2, §6) ------------------------
    /**
     * COPY-GAP (Rafi, T-189). Ivy's §5.2 slot table lists the ENTRY and PRIZE
     * eyebrows as "static" and her §15 register has no rows for them, so Theo's
     * deck does not carry them — but they are visible strings on the most-viewed
     * surface in the product, and architecture §14 rule 10 admits no exceptions.
     * Title case in the catalogue; the tile's all-caps is the `mono-xs`
     * treatment, matching every other label in this deck. **Flagged for Theo.**
     */
    "lobby.money.entry": "Entry",
    "lobby.money.prize": "Prize",
    "lobby.entry.free": "Free",
    /**
     * **The format chip** (Ivy lobby §23.2.2) — the two facts a tile states about
     * how the room plays, in one parameterised string.
     *
     * Authored by **Theo** (`design/wr-s11-d1-copy-deck.md`, Batch 1) and shipped
     * **verbatim** as Nora approved the wording, under the **DEX RULING D1-R1**
     * at the foot of that deck. `{rounds}` and `{seconds}` are positive integers
     * arriving from one `RoomFacts` projection (S11 spec 2) — never
     * pre-formatted, never a total.
     *
     * ── The `.one` sibling, and why it is not a clamp ──────────────────────────
     *
     * Nora's review found the one render state the deck's budget table never ran:
     * at `rounds === 1` this string says **"1 rounds"**. She named two closes and
     * Dex took **Close B** — the `.one` sibling below, her own string, character
     * for character. Close A (a `rounds >= 2` config pin) is **REJECTED**: room
     * spec §7.3 is LOCKED that *"B is never skipped for a one-round game"* and
     * `phase-machine.ts` implements it, so a one-round config is a ratified,
     * shipped product state and pinning it away at the config layer would orphan
     * a LOCKED clause and a live code path. **A diff that ships this pair AND a
     * `rounds >= 2` guard has taken the rejected close** (D1-R1 criterion 6).
     *
     * This is the house mechanism, stated at lines 659–674 of this file and now
     * applied a fifth time (`lobby.subtitle.live`, `profile.tx.amount.plus.a11y`,
     * `.minus.a11y`, `room.bots.offerCta`): **a `.one` sibling the caller selects
     * at `n === 1`. Not a `{count}` param and not a plural rule inside `t()`.**
     * The selection lives at the tile-face composition site
     * (`apps/web/src/lib/lobby.ts` → `tileFormatLabel`), which is rendering, not
     * composition: it reads `rounds` off the single `RoomFacts` object it was
     * handed and chooses which key renders it, sourcing nothing (D1-R1 (a)).
     *
     * ── Budget and treatment ──────────────────────────────────────────────────
     *
     * §22.14 budget is **≤ 22 characters** for the whole rendered chip, asserted
     * in this package's suite. `3 rounds · 90s` is 14; the deliberate worst case
     * `12 rounds · 180s` is 16; `1 round · 180s` is 14.
     *
     * The `·` is a **bare** separator with no non-breaking space on either side —
     * that is what lets the chip **wrap to two lines rather than shed a fact** at
     * the 195 floor (§23.2.2, LOCKED).
     *
     * **The rendered chip is UPPERCASE and that is the ramp, not the string.**
     * Ivy specifies `mono-xs`, and `.wr-mono-xs` bakes in
     * `text-transform: uppercase` — so this ships as `3 ROUNDS · 90S`, exactly as
     * `lobby.money.entry` ships as `ENTRY`. The catalogue keeps sentence case;
     * the treatment is the tile's.
     */
    "lobby.tile.format": "{rounds} rounds · {seconds}s",
    /**
     * The `n === 1` sibling — **Nora's own Close B string, verbatim.** It carries
     * **no `{rounds}` token** and does carry `{seconds}`: at `n === 1` the number
     * is fixed, which is the same call `lobby.subtitle.live.one` and
     * `profile.tx.amount.plus.a11y.one` already make.
     */
    "lobby.tile.format.one": "1 round · {seconds}s",
    /**
     * **LOCKED shape (§5.2): never "PRIZE 0".** Theo's rationale is worth
     * keeping: the fix is not a softer way to say "nothing", it is naming the
     * thing a free room actually pays out.
     */
    "lobby.prize.none": "Just the glory.",
    /** The `→` is a decorative suffix the component adds — never part of the string. */
    "lobby.cta.join": "Join",
    "lobby.cta.firstIn": "Be first",
    "lobby.cta.queue": "Next up",
    "lobby.status.nextRound": "Next round",
    "lobby.status.locked": "Locked",
    "lobby.status.closed": "Closed",
    /**
     * COPY-GAP (Rafi, T-210) — Ivy's fifth tile state, §6.7 / §20.1, added
     * 2026-07-29. Her interim string, carried verbatim. **Flagged for Theo.**
     *
     * It states the **player's own past action**, not a room condition, and that
     * is the whole reason it is not `lobby.status.closed`: "you left this one" is
     * a reason a 15-year-old accepts without a lecture about idempotency keys.
     */
    "lobby.status.left": "You left",
    // ---- locked tile (§6.4) --------------------------------------------------
    "lobby.locked.short": "need {n} more",
    "lobby.locked.getCoins": "Get coins",
    "lobby.locked.guestCta": "Create a free account",
    // ---- the `left` tile (§6.7, new 2026-07-29) ------------------------------
    /**
     * **Re-ratified 2026-08-02 (copy deck §S6 SUPERSEDING addendum, from Ivy
     * §22.7.6).** 52 ch.
     *
     * ── Two false mechanisms in a row, and why this one is not a third ─────────
     *
     * The T-213 string said the table reopens *"when it fills or times out"*, which
     * **A63** made wrong: an emptied room is now retired on its own clock in ~6 s.
     * T-251 replaced it with *"in seconds"*, and Theo's own §S6 addendum then
     * withdrew that:
     *
     * > *"A63 fires when the room the player left **empties**. It does not fire
     * > when someone else is still seated. A 1v1 where the opponent stays at the
     * > table is the flagship case for this exact tile, and in that case the room
     * > isn't empty — it turns over the old way, on fill or the unchanged 15-minute
     * > timeout. 'In seconds' is true for the empty case and off by roughly two
     * > orders of magnitude for the occupied case, on a muted tile the player is
     * > staring at while nothing happens."*
     *
     * **"Closes" is the one word true of all three exits** — it fills and starts,
     * it empties and A63 retires it, or it sits partial until the 15-minute
     * timeout — without naming a number, a mechanism or a speed. It does not need
     * re-litigating the next time the backend changes *how* rooms turn over, only
     * *whether* they still do.
     *
     * The character budget that shaped both earlier passes is **gone**: Ivy §22.7
     * gave this slot its own full-width wrapping row, so the sentence is sized by
     * meaning rather than by a `nowrap` we chose.
     *
     * **What it may not say — LOCKED (§6.7), all three still hold.** No coin
     * language: the refund was disclosed in the room and animated into the coin
     * chip, and restating it on a muted tile invites the player to wonder whether
     * it really happened. No exact time promise — and this string now carries no
     * time unit at all. No penalty framing: leaving pre-game is free, sanctioned
     * and fully refunded, and the tile must not read as a punishment for using a
     * control we told them was free.
     *
     * The opening clause is reused verbatim, keeping continuity with
     * `lobby.status.left` ("You left") on the same tile family. Reused verbatim by
     * the room screen as the disabled-CTA line when `Join again` / `Play again` is
     * refused (room spec §9 T4) — one string, two surfaces, so they cannot drift.
     */
    "lobby.left.note": "You left this one. A new one opens when it closes.",
    /**
     * **Re-ratified 2026-08-02 alongside its sibling (copy deck §S6 addendum).**
     * 63 ch, budget ≤76.
     *
     * This key's purpose cell says it carries *"the same two facts as
     * `lobby.left.note`"*, so it takes the sibling's wording verbatim — Theo
     * re-worded it in the same addendum rather than letting the pair drift out of
     * sync a third time (they already did once in T-213 and once in T-251). It
     * never picked up the "in seconds" defect, but it had the same omission: it
     * did not account for A63's fast path either.
     *
     * The 409 `seat_closed` toast — the same two facts as `lobby.left.note`, in
     * one line. It fires on the lobby (§12.4) **and** on the room screen's
     * terminal panels (room spec §11.4), and it is deliberately one key: two
     * strings for one fact is how two screens end up disagreeing.
     */
    "lobby.join.leftAlready": "You already left this table. A new one opens when it closes.",
    /**
     * **`JC-1`'s refusal — `D4` candidate 1, Nora's gate 2026-09-07: "SHIP
     * UNCHANGED".** The 409 `already_in_live_game` sentence: this player holds a
     * live seat at **another** table (Juno Ruling #15 §3).
     *
     * **Why it predicates possession and never activity — Nora's deciding
     * constraint, and she called it grammatical rather than stylistic:** *"'have a
     * seat' predicates possession of a row; 'seated' predicates a body in a
     * chair."* This refusal fires in two real states — genuine concurrent play,
     * and a stale seat whose player is not at it — and a sentence asserting
     * activity ("you're playing another game right now") is **false** in the
     * second, which is the worst version of a false-positive refusal. Possession
     * is true in both, because the row exists whether or not anyone is at it.
     * That is why the runner-up (*"You're already seated at another table."*) was
     * rejected and why no candidate offers a CTA: in the stale case there is
     * nothing to return to, and an instruction that cannot be honoured is worse
     * than none.
     *
     * **Reads as a pair with `lobby.join.alreadySeatedHere` by construction** —
     * same subject, same verb, same possession fact; only the determiner and the
     * preposition move (*this* → *another*). The two are never composed on one
     * screen, and Nora confirmed the contrast survives being **heard alone, in a
     * 4 s toast, with no context**.
     *
     * **Return trigger (Nora):** if Ivy ever adds a return-to-your-game
     * affordance, this string comes back to her — *"a sentence that sits above a
     * working link wants different shaping."*
     */
    "lobby.join.alreadyInLiveGame": "You already have a seat at another table.",
    /**
     * **The 409 `already_seated` refusal — `D4`'s retrofit rider, PRE-GATED by
     * Nora 2026-09-07 (her REPLACE-WITH, verbatim).**
     *
     * Minted here in `JC-1`'s wave because it was cheap: the sentence lived as a
     * raw literal in `apps/api/src/routes/rooms.ts`, was named by no test, and
     * `git grep` found exactly one occurrence. It said *"You already have a seat
     * in that room."* — **"room"**, where `lobby.headline`, `lobby.join
     * .leftAlready`, `room.noSeat.body` and `room.finished.body` all say
     * **"table"** (Theo's `D4` §1 finding: an engineering literal that leaked into
     * a player-visible string and never passed a copy review).
     *
     * Nora's deck changed **two** words, and the second is the point: *"you don't
     * have a seat *in* a table"* — the room→table swap had left its preposition
     * behind. *this* over *that*, because this/another is the cleaner opposition
     * with the sibling above and it matches `room.noSeat.body`'s register.
     *
     * Fires when a player taps the tile whose room they are **already seated in**
     * — the per-room predicate `reserveSeatAndCharge` owns (R15 §1: *"F-4 does not
     * let one `playerId` hold two seats in **one** room"*), which `JC-1` left
     * exactly as it was.
     */
    "lobby.join.alreadySeatedHere": "You already have a seat at this table.",
    // ---- toasts (§12) --------------------------------------------------------
    /**
     * `lobby.join.soon` **was deleted here in T-210** (S4-7), as §12.1 LOCKED it
     * to be: *"this toast is deleted when S4-5 lands, not left behind as a
     * fallback"*. The tap is a real join now, and a screen that keeps a
     * "coming soon" path beside a working one eventually shows the wrong one.
     */
    /** Mounts on Room Detail, never on the lobby (§12.3, LOCKED). */
    "lobby.redirect.toast": "That room filled up — you're in the next one.",
    // ---- empty / error / loading (§11) --------------------------------------
    "lobby.empty.title": "No tables open right now",
    "lobby.empty.body": "Come back in a bit, or hit refresh below to check again.",
    "lobby.error.body": "Couldn't load the tables. Check your connection and try again.",
    "lobby.loading": "Loading tables…",
    // ---- accessibility (§13) -------------------------------------------------
    /**
     * The tile's accessible name, composed from ONE parameterised string (§13
     * item 1) — never concatenated.
     *
     * **Theo's flag #2, implemented: `{entry}` and `{prize}` arrive as
     * fully-formed phrases, not bare numbers.** The template does not hard-code
     * the word "coins" around them, because the zero cases pass
     * `lobby.entry.free` and `lobby.prize.none` instead — otherwise the
     * accessible name would say "entry 0 coins" and "prize 0 coins" on exactly
     * the two tiles whose visible face is LOCKED never to show that (§5.2), and
     * the screen reader would contradict the screen.
     */
    "lobby.tile.a11y": "{type}, {mode}. {playerCount} of {maxPlayers} players. Entry {entry}, prize {prize}. {state}.",
    /**
     * COPY-GAP (Rafi, T-189). The composed phrase Theo's wiring note requires
     * ("pass `25 coins`"), which his register has no slot for because it is an
     * a11y fragment rather than a visible string. Written to his instruction
     * verbatim. **Flagged for Theo to ratify.**
     */
    "lobby.coins.amount": "{n} coins",
    /* ========================================================================
       The daily reward row — §21 (DR-5, T-314)
       ========================================================================
     * Ivy's `wr-s3-lobby-spec.md` §21.7 register, written by **Theo**
     * (`wr-daily-reward-copy.md`, 2026-08-06) and **Nora-reviewed the same day:
     * SHIP WITH DECK — 8 keys ship as written, 3 change.** The three changed ones
     * carry her reason inline; every string below is her verdict applied, never
     * Theo's table as first written.
     *
     * ── Key naming: `needsPlay`, not `needs_play` ──────────────────────────────
     *
     * Theo's deck writes the key as `lobby.daily.needs_play` in its table and as
     * `lobby.daily.needsPlay` in its own a11y wiring note, four rows later. This
     * catalogue's rule decides it: keys are **camelCase per segment**, which is
     * the deck's own stated house rule and the transform every prior deck took
     * (`lobby.type.open_count` → `lobby.type.openCount`, `lobby.cta.first_in` →
     * `lobby.cta.firstIn`, and `resets_soon` → `resetsSoon` in this very deck).
     * A key path is not a string, so Nora's gate is untouched by this.
     *
     * ── What is NOT here ───────────────────────────────────────────────────────
     *
     * There is no `disabled` copy: that state renders **no node at all** (§21.3
     * rule 1, LOCKED) and neither does any state this client does not handle
     * (§21.3 ADDENDUM 2026-08-07).
     */
    /**
     * `needs_play` centre. **CHANGED by Nora from Theo's `…today's {n}.`** — the
     * param is gone and that is the point: the leading slot renders the same
     * number eight words earlier, so the row said `● 50   Play one game to unlock
     * today's 50.` and the accessible name said it twice in one utterance. Ivy
     * states the rule herself one row down (`claimable` "must not restate the
     * amount — the leading slot has it"); applying it here is consistency, not a
     * new constraint.
     *
     * The three account states now share one noun phrase — *today's coins* —
     * across a day of play: **unlock** it, it is **ready**, it is **claimed**.
     * 38 ch / ≤ 46.
     */
    "lobby.daily.needsPlay": "Play one game to unlock today's coins.",
    /** `claimable` centre. Ships as written. Doesn't restate the amount, doesn't command — the button is the command. 24 ch / ≤ 34. */
    "lobby.daily.claimable": "Today's coins are ready.",
    /**
     * The claim button. Ships as written, **case-corrected from Ivy's DRAFT
     * `CLAIM`**: every label in this catalogue is sentence-case in the string with
     * the all-caps treatment applied by CSS (`lobby.cta.join`, `nav.soon`,
     * `role.guest`). 5 ch / ≤ 10.
     */
    "lobby.daily.claim": "Claim",
    /**
     * **The credited moment, and the ONE string in this product licensed to say
     * "you"** (§21.4 LOCKED). It renders only while this client's own claim POST
     * returned `credited === true`; the row settles to `lobby.daily.claimed` on
     * the next state read.
     *
     * **CHANGED by Nora from Theo's `{n} added. Come back tomorrow.`** — that
     * opening is a bank notification (agentless, passive, ledger verb) sitting in
     * the one slot the anti-leak structure deliberately made personal. `credited`
     * is *ours and personal*; `claimed` is *about the day and impersonal*, and
     * with "50 added." the two differed mainly in tense.
     *
     * No constraint moves: the pronoun ban is on `claimed`, not here. No time, no
     * streak, no count, no exclamation mark. 31 ch at n=50 / ≤ 44.
     */
    "lobby.daily.credited": "You got {n}. Come back tomorrow.",
    /**
     * `claimed` centre. Ships as written — and it is **deliberately the dullest
     * string in the deck**.
     *
     * §21.4 LOCKED: `aaron+1@` and `aaron+2@` share one mailbox-day, so the
     * account that did **not** claim sees this exact line. No pronoun, no name, no
     * time, no "already", no "again", no amount-received phrasing — it is true
     * whichever account on the mailbox claimed. Any word added to warm it up is a
     * word that leaks the alias sibling. 26 ch / ≤ 34.
     */
    "lobby.daily.claimed": "Today's coins are claimed.",
    /**
     * `ineligible_guest` centre — A103 §8's conversion pitch.
     *
     * **CHANGED 2026-08-07 (Theo, NORA-CLEARED then re-reviewed in her own
     * addendum) from `Create a free account for daily coins.` (38 ch)**, which
     * Quinn measured on the deployed row at 320 px rendering `Creat…` — the act
     * intact, the offer gone (T-318 finding N-1).
     *
     * It states a **product fact**, not a causal promise: no "today", and no
     * implication that converting alone pays — verification and one game still
     * stand in the path (§21.3 rule 3, LOCKED). Do not "improve" this into a
     * promise, and note that the honesty rule now rests on this sentence alone:
     * ADDENDUM C dropped the leading slot from this state, so *"a coin, a figure
     * and a conversion button with no sentence to condition them"* is exactly the
     * row Ivy refused. **Dropping or truncating this line is not a layout option.**
     *
     * It carries no `{n}` and never will — the number does not render in this
     * state at all (§21.14 C.1), so the sentence reads identically at 500 and at
     * Juno's ratified production 50.
     *
     * **22 ch / ≤ 24 ch, 133 px measured / ≤ 136 px @320** (ADDENDUM C C.4; the
     * cage was ≤ 44 ch and the pixel budget is the one that governs).
     */
    "lobby.daily.guest": "Free coins come daily.",
    /**
     * The guest button. **NORA-WRITTEN AND GATED 2026-08-07**, replacing her own
     * `Create account` (14 ch), which Quinn measured at **142 px** against
     * ADDENDUM C's ≤ 100 px budget — over by 42, and the widest thing on a row
     * whose centre line then had 43 px to live in.
     *
     * `Sign up` is 7 characters and **83 px measured** on the deployed button. It
     * is a verb where `New account` was a noun phrase, it collides with nothing on
     * the lobby (`Join` is *forbidden* by C.4 — `lobby.cta.join` is the room-join
     * verb on this very screen), and it does not repeat `free` from the centre
     * line beside it, which is why `Sign up free` lost.
     *
     * **The divergence from `auth.cta.signup` (`Create account`) is deliberate and
     * ruled** (Nora, C.6 item 1): that string avoids stuttering under an auth tab
     * already labelled *Sign up*; this one avoids a 236 px cage. One principle,
     * two neighbours — `auth.cta.signup` does **not** move, and these two keys are
     * not aliases.
     *
     * **The 17 px this label leaves unspent is banked as margin for C.5's
     * residual, not available to a longer string.** Sentence case here; the
     * button's existing treatment renders `SIGN UP`. 7 ch / ≤ 12 ch (guard 8),
     * 83 px / ≤ 100 px @320.
     */
    "lobby.daily.guestCta": "Sign up",
    /**
     * **NOT RENDERED — kept on purpose** (§21.3 ADDENDUM 2026-08-07, Ivy; Dex
     * §16o addendum 3 routed question 2, option (a)).
     *
     * `ineligible_unverified` is a real server state and an honest one, but it is
     * **unreachable by design**: Ivy's `wr-s2-design-spec.md` §14.1 Ruling A
     * (LOCKED, 2026-07-27) kills the guest session at conversion, and Better
     * Auth refuses sign-in to an unverified address, so no live client can hold a
     * session in this state. DR-5 therefore implements no branch for it and the
     * client's unknown-state fallback renders nothing.
     *
     * These two strings stay so that whoever reverses §14.1 inherits the copy, the
     * treatment and the button target for free. **Deleting them is not a cleanup**
     * — it is throwing away the paid-for half of a decision that is still live.
     *
     * **ADDENDUM C §21.14 C.6 item 3:** this pair is over the new budgets
     * (≤ 24 ch / ≤ 136 px and ≤ 12 ch / ≤ 100 px — `Verify your email for daily
     * coins.` is 33 ch) and is **recorded but NOT enforced while unrendered**; an
     * enforced budget on a string nobody can see would force a pointless rewrite.
     * Whoever reverses §14.1 re-cuts them with Nora as part of that work, and
     * inherits the same no-leading-slot composition (C.1 covers this state too).
     * **No copy ticket today.**
     */
    "lobby.daily.unverified": "Verify your email for daily coins.",
    /** NOT RENDERED — see `lobby.daily.unverified` above. */
    "lobby.daily.unverifiedCta": "Verify",
    /**
     * The countdown's last minute. **Deliberately lowercase** — §21.7 spells it
     * out (*"lowercase, `mono-xs`"*), and this is the one slot in the deck where
     * case is baked into the copy rather than applied by CSS. Not a typo.
     *
     * It is a phrase and never a seconds counter (§21.6 LOCKED): a per-second
     * ticker on a lobby is a heartbeat animation attached to a fact nobody needs
     * to that precision. 11 ch / ≤ 14.
     */
    "lobby.daily.resetsSoon": "resets soon",
    /**
     * The claim POST failed (network or non-2xx). Inline on the centre line, never
     * a toast (§21.8 item 4, §12.2). No apology word, no code, no "error" — the
     * same register as `welcome.rerollFailed`. 34 ch / ≤ 40.
     */
    "lobby.daily.failed": "That didn't go through. Try again.",
    /**
     * The row's accessible name in the two **non-interactive** states
     * (`needs_play`, `claimed`, and the credited moment). `claimable` and
     * `ineligible_guest` carry a real button and need no composed name.
     *
     * **`{line}` is the fully-rendered centre string, never a state enum** —
     * Nora's QUERY 1, accepted: her point is that a param named `{state}` sitting
     * beside a payload field named `state` is an invitation to pass
     * `"needs_play"` and ship *"Daily coins, 50. needs_play."* to a screen reader.
     * The rename makes the correct value the obvious one.
     *
     * Because `{line}` reuses `lobby.daily.claimed` verbatim, §21.7's *"must not
     * carry a time in `claimed`"* is satisfied **by construction** rather than by
     * a second discipline check, and the spoken row can never disagree with the
     * printed one. Same wiring as `lobby.tile.a11y`.
     *
     * Worst case (3-digit amount, `needs_play`): 56 ch / ≤ 90.
     */
    "lobby.daily.a11y": "Daily coins, {n}. {line}",
    /**
     * The countdown itself — §21.6's table, and **Ivy's own literals rather than a
     * Theo slot**: her deck is explicit that `amountCoins` and the countdown
     * figures are *data, interpolated as params*, and that no literal already
     * written into §21 prose was his to alter. They live here anyway because
     * architecture §14 rule 10 admits no string literal in JSX, not because they
     * are new copy.
     *
     * Whole hours / whole minutes, **rounded down**, updated at most once a
     * minute. A countdown is licensed here as the distance to a **stated future
     * instant the server supplied** — the exception §21.6 carves once, explicitly,
     * against S6 §5.2's ban on relative times, and it does not generalise.
     */
    "lobby.daily.countdown.hours": "in {n}h",
    "lobby.daily.countdown.minutes": "in {n}m",
    /* ========================================================================
       Room Detail (S4-7)
       ========================================================================
     * Ivy's `wr-s4-room-spec.md` §16 register, merged in T-210 as her interim
     * DRAFTs.
     *
     * **Ratified 2026-07-29 by Theo (T-213) and applied here in T-215.** 61 of
     * the 68 room keys were ratified verbatim and are unchanged; the replaced
     * ones carry a `Ratified … (replaced)` note with his one-line reason, so the
     * next person to edit a string reads why it says what it says. The grace
     * copy **split into three keys** (Ivy §11.3a) and the four `room.finished.*`
     * keys for **T8** (§9.9) were ratified in his two addenda. Nothing in this
     * block is Rafi's copy.
     *
     * ── Key naming, and why it is not Ivy's spelling ──────────────────────────
     *
     * Her register writes leaf segments in `snake_case`
     * (`room.waiting.ready_deadline`, `room.results.you_won`,
     * `room.all_disconnected.no_result`). This catalogue's convention is
     * camelCase per segment, and Theo's S3 deck already translated her lobby
     * register the same way 1:1 (`lobby.type.open_count` → `lobby.type.openCount`,
     * `lobby.cta.first_in` → `lobby.cta.firstIn`). The transform applied here is
     * that same one, mechanically, with no other change — so
     * `room.all_disconnected.no_result` is `room.allDisconnected.noResult`.
     *
     * `convert.blocked.in_game` remains the documented exception: it was carried
     * verbatim in T-174 and renaming it now would break the one key three people
     * search for by name. Flagged in the T-210 output so the register and the
     * catalogue can be read together.
     *
     * ── The constraints that are LOCKED on the *content*, not the key ─────────
     *
     * Repeated here because the next person to edit a string will read this file
     * and not the spec:
     *
     *  - `room.waiting.readyDeadline` **must disclose the auto-ready** (§6.3). A
     *    countdown that silently does something for you is the same defect class
     *    as a rendered `0` balance.
     *  - `room.forfeited.body` **must state both consequences** — the entry is not
     *    refunded **and** it counts as a loss — and "mid-game" in it is
     *    load-bearing (§9.0): it renders only for a seat that ended after the
     *    first round started, and it may not soften to "early".
     *  - `room.seatLost.refundNote` **must not mention forfeiting or losing.** The
     *    entry came back and nothing was recorded.
     *  - `room.closed.*` and `room.allDisconnected.*` are **two different
     *    apologies and must not converge** (§16 LOCKED for Theo). `closed` is *we*
     *    did this; `allDisconnected` is *the connection* did this to all of us and
     *    **nothing counts**. Neither set may borrow the other's cause sentence.
     *  - `room.allDisconnected.noResult` always renders — every fee, any number of
     *    completed rounds, and for a player who forfeited earlier in that room.
     *  - `room.grace.expired` **must not assert the forfeit happened.** Clock skew
     *    is real and the server is the authority; the true answer arrives as T3,
     *    T4, T7 or a live room, and the sentence has to survive all four.
     *  - `room.noSeat.body` **must not imply spectating is coming.** There is none
     *    in v1 (§9 T5, LOCKED).
     *  - `room.round.running` was a **stand-in and is deleted, as S4 §7.2 said it
     *    would be** (S5-4, 2026-07-30): the real board renders there now, and its
     *    loading state is `WordLoader` with no skeleton grid (game spec §5.10). A
     *    stand-in kept "just in case" is a second answer to what the screen is
     *    doing, which is the thing the note existed to prevent.
     */
    // ---- frame and status (§4.2, §11.1) -------------------------------------
    /** `<h1>` and the top bar's `<p>`. The room's whole public identity (§19 line 32). */
    "room.heading": "{type} · {mode}",
    "room.phase.filling": "Filling",
    "room.phase.readyCheck": "Ready check",
    "room.phase.round": "Round {n}",
    "room.phase.final": "Final",
    "room.status.ready": "Ready",
    "room.status.waiting": "Waiting",
    "room.status.reconnecting": "Reconnecting",
    /**
     * The seat pill for a seat that ended pre-game. Distinct from
     * `lobby.status.left`, which is a *tile* state about the viewer's own action;
     * this one names another player's seat.
     */
    "room.status.left": "Left",
    "room.status.forfeited": "Forfeited",
    /*
     * **`room.team.name` ("Team {letter}") was RETIRED here by S12 R2** — D1
     * ruling `wr-s12-d1-team-strings.md`, Decision 1, Nora-noted:
     *
     * > *"`room.team.name` is the resolved output of `teamLetter()`/`teamLabel()`
     * > … The letter-generation mechanism itself — not just its output string —
     * > is what SIDES exists to retire; a rename would keep the wrong mechanism
     * > alive under a new label. There is no copy gap to fill: the replacement
     * > text already exists and already passed Nora's review."*
     *
     * **Nothing was minted to replace it.** Its four render sites (`TeamBlock`,
     * `Scoreboard`'s `team.label`, `ScoreStripCell.label`, `winnerName()`'s team
     * branch) all repoint to the four `side.N.name` keys below, keyed by
     * `sideIndex` instead of computed as a letter. Do not re-add it.
     */
    /* ------------------------------------------------------------------------
       The four side names — D1 copy deck **Batch 3**, Nora-approved (SHIP),
       landed verbatim by S12 R1 `SIDES-CORE`.
       ------------------------------------------------------------------------
     * One key per row of the frozen `SIDES` table in `@wr/contracts`, whose
     * `nameKey` cell is these four keys and nothing else. Key spelling matches
     * Dex's spec 3 exactly (numeric `sideIndex` segment, dot path) — this is the
     * documented exception to the camelCase leaf convention, for the same reason
     * `convert.blocked.in_game` is: the canonical key is copied, not translated.
     *
     * **Ivy's D3a §1 pins the colour each word must be true to** — 0 `play`
     * (periwinkle), 1 `warn` (coral), 2 `good` (mint), 3 `lilac`. Two binding
     * constraints from her, both satisfied here:
     *
     *  - **hex precision does not leak into copy.** `warn` is never "Coral" and
     *    `good` is never "Mint" in any player-facing string; the speakable word
     *    for a fifteen-year-old is Blue, Red, Green.
     *  - **`side.3.name` is "Purple".** The deck's first draft shipped `"Yellow"`,
     *    predicting `coin` would be in the four; `coin` is excluded because it is
     *    the money colour. The deck records that supersession itself, and a build
     *    finding `"Yellow"` here has picked up the superseded string.
     *
     * **Never counted, never pluralised** (there is no "two Blues"), and the name
     * renders **only** where `team_size > 1` — spec 3, LOCKED: a team of one is a
     * player, so a 1v1 shows two side colours and no name at all. That is why none
     * of the four needs a `.one` sibling: the slot it would occupy does not exist
     * at that team size. Possessives hold as a plain `'s` on all four.
     *
     * **Unrendered as of R1** — R2 (`SIDES-RESULTS-SPLIT`) is the first surface to
     * resolve them, and R1's own non-goals forbid it rendering any chrome. They
     * land here now because the table names them and a table pointing at keys that
     * do not exist is not a table. Precedent: the unrendered verification pair
     * already in this catalogue.
     */
    "side.0.name": "Blue",
    "side.1.name": "Red",
    "side.2.name": "Green",
    "side.3.name": "Purple",
    "room.vs": "VS",
    /** Marks the viewer's own seat and their scoreboard row (§6.2, LOCKED). */
    "room.seat.you": "You",
    "room.seat.open": "Open seat",
    // ---- waiting (§6) --------------------------------------------------------
    "room.waiting.filling": "Waiting for {n} more.",
    "room.waiting.full": "Everyone's in.",
    /** §6.3 LOCKED: the auto-ready is disclosed in the same sentence as the clock. */
    "room.waiting.readyDeadline": "Starting in {time} — you'll be readied automatically.",
    /** §6.4: rendered only inside the warning window, never on arrival. */
    "room.waiting.fillDeadline": "This table closes in {time} if it doesn't fill.",
    /** §6.4: always present at fee > 0, and never rendered at fee 0. */
    "room.waiting.refundNote": "Leave now and your {value} comes straight back.",
    /**
     * **§22.19.10 (T-355, RATIFIED VERBATIM) — the alone-note.** The quiet
     * second-line fact for a player seated alone in a `filling` room where no offer
     * is ever coming: `!botOfferPossible` is its trigger, not its veto (§22.19.9).
     * Its main live surface is a **funded player alone at a paid table** — rule
     * 32(a) makes `botOfferPossible` false there forever, up to a 15-minute silence
     * with coins staked. Pure present-fact — no bot, no digit, no clock, no
     * forward-looking claim — so it survives the whole wait, and at 19 characters
     * it cannot wrap at any supported width (§22.14 satisfied vacuously).
     */
    "room.waiting.aloneNote": "You got here first.",
    "room.cta.ready": "I'm ready",
    "room.cta.readyDone": "You're ready",
    "room.cta.leave": "Leave",
    // ---- round and between rounds (§7) --------------------------------------
    "room.round.counter": "Round {n} of {total}",
    "room.round.timerLabel": "Time left",
    "room.between.heading": "Round {n} done.",
    "room.between.nextIn": "Next round in",
    "room.score.round": "This",
    "room.score.total": "Total",
    /**
     * **"Team total" → "Side total"** — D1 ruling, Decision 2, **Nora PASS
     * 2026-08-23** (*"Read aloud, 'Red · Side total' is something a person says."*).
     *
     * Composed at its render site as `{sideName} · {this}` (`Scoreboard`'s team
     * cell), so leaving it as "Team total" would have rendered **"Red · Team
     * total"** the moment R2 put a side name in the first half — the same
     * collision the S11 deck flagged, with a fixed noun instead of a letter. It
     * cannot be retired outright either: none of the four `side.N.name` strings
     * means *"this row is the side's combined score"*.
     *
     * **The key identifier moved with it** (`room.score.teamTotal` →
     * `room.score.sideTotal`). D1 leaves that to the builder — *"the key name is
     * never player-visible"* — and the rename is taken so that Nora's own closing
     * check stays mechanical: after R2 the word "team" does not appear in this
     * catalogue's player-visible surface **at all**, key or value, which is a
     * grep anyone can re-run rather than a judgement about which hits count.
     */
    "room.score.sideTotal": "Side total",
    // ---- results (§8) --------------------------------------------------------
    /**
     * The **decided** result's two halves. A draw no longer reaches either of
     * them — see `room.results.tied` below.
     */
    "room.results.youWon": "You won.",
    "room.results.otherWon": "{winner} won.",
    /**
     * **The drawn game's headline — Theo's 2026-08-02 addendum (T-250 NB5's
     * closure), which AMENDS `wr-s4-room-spec.md` §8.2's LOCKED note.**
     *
     * §8.2 originally ruled that no separate tie heading exists, because *"'you
     * won' is true in a tie… a special string would make a shared win read as a
     * lesser one"* — and at the time the product agreed with itself: a draw paid
     * out and was credited exactly like a win.
     *
     * Juno's later rulings moved underneath that note. **R7 §5** resets the
     * current streak to 0 on a draw (the same reset a loss produces) and **R9**
     * excludes draws from the win-rate denominator entirely. So a player who read
     * "You won." then watched their streak reset and their rate not move was
     * being told two different things by one event, and the note's own worry had
     * flipped: the shared string had become the one claiming more than the rest
     * of the product would stand behind.
     *
     * The amendment was pre-authorised by the architecture's fold list, which
     * named this exact mechanism (*"Theo + Juno copy/rules call, then a one-string
     * change"*). **§8.2's winner-set rule is untouched** — the server decides
     * winners, the client never derives them; only the tied case's heading text
     * changes.
     *
     * *"Tie", not "draw"*, deliberately: "draw" is the economy rulings' internal
     * term, and this string shares its vocabulary with
     * `profile.stats.winRate.note` ("Ties aren't counted.") so a player builds one
     * word for the concept rather than two.
     */
    "room.results.tied": "It's a tie.",
    "room.results.standings": "Final standings",
    /** `{value}` is the **credited** amount — the number in `coin_transactions`. */
    "room.results.youGot": "{value} is in your wallet.",
    "room.results.prizeSplit": "Split {n} ways — {value} each.",
    /** The viewer was not credited. **No negative delta anywhere** (§8.3, LOCKED). */
    "room.results.prizeTo": "The prize went to {winner}.",
    "room.cta.playAgain": "Play again",
    "room.cta.backToLobby": "Back to the lobby",
    /**
     * COPY-GAP (Rafi, T-210) — **not in Ivy's §16 register, and it needs to be.**
     *
     * §9's T4 row specifies the panel's primary as `Join again` (with a cost chip
     * at fee > 0) and §9's "T4's CTA" sub-section discusses it at length, but no
     * key was slotted for the label. S2's `SeatReleasedPanel` took its label as a
     * prop and `/dev/room` passed `devRoom.rejoin` — a developer-surface string
     * that must not become product copy by reuse. (That screen and its strings are
     * gone as of §16f A60; this key is why nothing had to be recovered from them.) Written to Ivy's own wording and
     * Theo's tone brief. **Flagged for Theo, and for Ivy to add a register row.**
     */
    "room.cta.joinAgain": "Join again",
    // ---- leave dialog (§10) --------------------------------------------------
    "room.leave.title": "Leave this game?",
    /** §10.1 LOCKED: **both** consequences, always. */
    "room.leave.body": "You'll lose your {value} entry and it counts as a loss.",
    /**
     * Fee 0 — the money consequence is zero, the stat consequence is not.
     *
     * **Ratified 2026-07-29 (T-213, replaced).** The DRAFT ("Nothing to lose but
     * the game — it counts as a loss.") played "lose" against "loss" at a
     * decision point where §10.1 LOCKS the stat consequence to land clearly even
     * when the money one is zero; a skimming reader takes "nothing to lose" as
     * reassurance and misses the disclosure. 54 ch / budget 110.
     */
    "room.leave.bodyFree": "No coins are at stake — but it still counts as a loss.",
    /**
     * §10.3: offline, still forfeits, seat held until the window ends.
     *
     * **Ratified 2026-07-29 (T-213, replaced).** The DRAFT's "a little longer"
     * did not satisfy §10.3's LOCKED requirement to say the seat is held *until
     * the grace window ends* — it reads open-ended at the exact moment a player
     * is deciding whether to wait it out. 111 ch / budget 130.
     */
    "room.leave.bodyOffline": "You're offline. Leaving now still forfeits your entry, and your seat stays held only until the grace window ends.",
    /** **Primary.** The destructive option is never the primary (§10.1, LOCKED). */
    "room.leave.stay": "Keep playing",
    "room.leave.confirm": "Leave and forfeit",
    // ---- terminal and interrupted panels (§9) -------------------------------
    /**
     * **T1, all three ratified 2026-07-29 (T-213, replaced).** T1 covers **two**
     * causes (§9's table: fill timeout *or* config disabled), and the DRAFTs
     * ("This table didn't fill." / "Nobody else showed up in time.") were true
     * only for the first: a room that reached `ready_check` and was then
     * cancelled by a config change *did* fill. Cause-neutral phrasing is true on
     * every path T1 is reached from, which is also what §9.7 relies on when it
     * makes T1 the universal fallback for an unknown cancel reason. 24 / 62 / 40
     * ch against budgets 30 / 110 / 90.
     */
    "room.cancelled.title": "This table closed early.",
    "room.cancelled.body": "It closed before the round could start. Your {value} is back.",
    "room.cancelled.bodyFree": "It closed before the round could start.",
    "room.closed.title": "This table was closed.",
    "room.closed.body": "We had to close this room. Your {value} is back.",
    "room.closed.bodyFree": "We had to close this room.",
    "room.forfeited.title": "You're out of this one.",
    /**
     * **T3, both ratified 2026-07-29 (T-213, replaced).** T3 fires for two
     * causes (§9's table): a confirmed mid-game leave, **or** a grace expiry
     * after a drop — and "You left mid-game" is false for the second, where the
     * player chose nothing. Theo reused the house pattern already ratified for
     * the identical ambiguous-cause case at T4 ("You stepped away…") rather than
     * inventing a voice. **"mid-game" is intact per the LOCKED note** (§9.0):
     * only the verb changed. 86 / 52 ch against budgets 130 / 110.
     */
    "room.forfeited.body": "You stepped away mid-game, so your {value} stays in the pot and it counts as a loss.",
    "room.forfeited.bodyFree": "You stepped away mid-game, so it counts as a loss.",
    /** T4, appended to S2's reused panel at fee > 0. No forfeit or loss language. */
    "room.seatLost.refundNote": "Your {value} is back.",
    "room.noSeat.title": "You're not in this room.",
    "room.noSeat.body": "This table belongs to other players.",
    "room.error.body": "We can't load this room right now.",
    "room.loading": "Getting the room ready",
    "room.allDisconnected.title": "Everyone lost connection.",
    "room.allDisconnected.body": "The room closed because every player dropped out. Your {value} is back.",
    "room.allDisconnected.bodyFree": "The room closed because every player dropped out.",
    /** **Always renders** (§9.7, LOCKED). The one fact nothing else on screen carries. */
    "room.allDisconnected.noResult": "This one doesn't count — no win, no loss.",
    // ---- T8 · this game is over (§9.9, new 2026-07-29) -----------------------
    /**
     * **T8, all four ratified verbatim 2026-07-29 (T-213 addendum 2).**
     *
     * The room-that-finished-without-you panel, reachable by two routes (§9.9): a
     * drop with less than `reconnect_grace_s` left in the game — the seat never
     * forfeits and the prize is credited — and returning to a disposed room from
     * history. The client cannot tell them apart and does not need to.
     *
     * **What this panel may not claim — LOCKED (§9.9).** It answers exactly two
     * questions: *is this room gone* and *did anything happen to my money*. No
     * standings, no placement, no scores, no crown, no `PrizePanel`, no `Play
     * again`, **no loss language and no stat claim** (we do not record wins and
     * losses until S6). "Won" is licensed in `prizeYou` and nowhere else: §8.2
     * makes a `room.prize` credit a win by definition.
     *
     * **The free-room gap is deliberate and recorded** (§9.9): at prize 0 there
     * is no ledger row (§5.3 — `amount_delta` is never 0), so nothing the server
     * can read back says whether a free game was won. T8 therefore says nothing
     * about a free game's outcome rather than guessing. Flagged for S6.
     */
    "room.finished.title": "This game is over.",
    "room.finished.body": "It finished and the table has closed.",
    /**
     * Paid rooms only, viewer credited. `{value}` is **the credited amount** —
     * the `room.prize` transaction, never the room's prize snapshot (§8.3's
     * one-truth rule, §17 note 9).
     */
    "room.finished.prizeYou": "You won {value} — it's in your wallet.",
    /**
     * Paid rooms only, viewer not credited. **Names no player** — T8 carries no
     * standings to name from — and no loss language: stating where the prize went
     * is not asserting that the viewer lost.
     */
    "room.finished.prizeOther": "The prize went to another player.",
    // ---- recovery (§11.3, §11.4) --------------------------------------------
    "room.grace.countdown": "Back in {time} or you forfeit {value}.",
    /**
     * **New key, ratified verbatim 2026-07-29 (T-213 addendum 1, Ivy §11.3a).**
     *
     * Pre-game only — `filling` / `ready_check`, **any fee**. Nothing here is at
     * risk but the seat, so there is **no forfeit language and no loss
     * language**, and it neither asserts nor contradicts a refund: that fact is
     * already on screen one element above (`room.waiting.refundNote`) and lands
     * again on T4.
     *
     * It exists because one key was carrying two *opposite* disclosure duties
     * (§11.3a): pre-game expiry lands on **T4** (refunded, nothing recorded) and
     * mid-game-at-fee-0 expiry lands on **T3** (out, and a loss recorded). No
     * 60-character string discharges both. **The three grace keys must not be
     * interchanged.**
     */
    "room.grace.countdownPregame": "Your seat is held for {time}.",
    /**
     * **Narrowed 2026-07-29 (§11.3a) and ratified with the loss clause added
     * (T-213 addendum 1): mid-game at fee 0 only** — the `_free` variant of
     * `room.grace.countdown`, which is what `_free` means for every other key in
     * this register. It states the player is out **and that it counts as a loss**
     * (§10.1's rule: the stat consequence is disclosed even when the money one is
     * zero), with **no money language where there is no money**. 51 ch / ≤60.
     *
     * **Quinn: this followed by a T7 panel saying "no win, no loss" is correct**
     * (§11.3a) — the countdown is a warning about the ordinary consequence while
     * the player can still prevent it; T7 is a room-level result that overrides
     * it.
     */
    "room.grace.countdownFree": "Back in {time} or you're out — it counts as a loss.",
    /** Local zero. It says the seat *may* be gone, because the server is the authority. */
    "room.grace.expired": "Your seat may be gone. Reconnecting…",
    "room.caughtUp": "You're back — here's where things are.",
    // ---- accessibility (§13) -------------------------------------------------
    /**
     * **One key per seat-row SHAPE — Ivy §22.8, ruled 2026-08-02.**
     *
     * > *"A 'shape' is the set of clauses present, not a status word. Status words
     * > stay parameters, so §11.1's vocabulary can grow without minting a key."*
     *
     * **Clause order is fixed: position → identity → viewer marker → state.** It
     * mirrors the card's own visual reading order (seat block, avatar + name, the
     * `You` line beneath the name, the status pill at the trailing edge), so a
     * screen-reader user and a sighted user assemble the row in the same sequence.
     *
     * `{status}` is fed **only** from `seatStatus()`'s resolved word — `Ready`,
     * `Waiting`, `Reconnecting`, `Left`, `Forfeited`. Never a raw enum, never a
     * derived phrase, never a sentence. Selecting between the three is a pure `if`
     * on `(empty, isYou)`: no concatenation, no interpolating one resolved string
     * into another.
     *
     * **DRAFT — Theo to ratify** on the pre-profile copy pass (§22.8.2). His
     * ratification rides that pass and does not block this build. His noted rider:
     * the status words are capitalised for a pill and land mid-sentence here; he
     * may want lowercase variants for the a11y register, and the pill's word does
     * not change either way. The capitalised form ships now.
     *
     * Occupied, someone else. Unchanged since §13 item 2 — the shape it always
     * described correctly.
     */
    "room.seat.a11y": "Seat {seat}: {nickname}, {status}.",
    /**
     * Occupied, **the viewer**. New (§22.8.2), DRAFT.
     *
     * §6.2 LOCKED the viewer's seat as *"marked by a word, not only by position or
     * colour"* — and the word was the one thing the old single key omitted, because
     * the visible `You` chip is `aria-hidden` and the label did not restate it. A
     * screen-reader user could not tell which seat was theirs.
     *
     * The marker is a **clause, not a node**: it is restated here rather than
     * un-hidden on the card, because an `aria-label` overrides its whole subtree
     * and un-hiding the chip would put the same word in the announcement twice.
     */
    "room.seat.a11y.you": "Seat {seat}: {nickname}, your seat, {status}.",
    /**
     * An empty slot. New (§22.8.2), DRAFT.
     *
     * **It carries no status clause**, and that is the fix as much as the key is:
     * the builder used to pass `room.seat.open` as *both* `{nickname}` and
     * `{status}`, so the announcement was *"Seat 3: Open seat, Open seat."* §6.2
     * already ruled *"there is nobody to have a status"*, and this label now agrees
     * with the pill's absence.
     */
    "room.seat.a11y.open": "Seat {seat}: open seat.",
    /**
     * A final standings row, spoken — so a screen-reader user gets the prize
     * without seeing the chip (§13 item 4).
     *
     * ── The template is UNCHANGED; what changed is what is fed to it (D2, T-568) ─
     *
     * **`{place}` is delivered as an ordinal WORD ("First", "Second", …), never
     * the cardinal integer** — ruled at
     * `projects/wordy-royale/wr-s14-d2-standings-name-recut.md` §1c. The visual
     * place numeral is bare and `aria-hidden` because its column and its sort
     * order already say *"this is a rank"*; the spoken name is one sentence with
     * no such context, so it says the word. **One derivation** — the ordinals are
     * `room.standing.place.N` below and `placePhrase()` in `apps/web/src/lib/room.ts`
     * is the only reader; a second cardinal-to-ordinal conversion inlined
     * elsewhere is the same defect D2 closed.
     *
     * **`{prize}` is `coinsPhrase(prizeCoins)`** — `lobby.coins.amount`, the one
     * place in this codebase that turns a coin integer into words (Dex,
     * Addendum 7 §1a). **No template in this family spells "coins" itself.**
     *
     * ── SIX keys, and the shape is the house pattern, not scope creep ──────────
     *
     * `t()` does flat substitution with no conditionals inside a string, and this
     * catalogue's ratified answer to an optionally-present clause is **whole keys,
     * one per shape** (`en.ts`'s lead clause, §Q-2). So:
     *
     * |            | `score !== 1`             | `score === 1`                 |
     * |------------|---------------------------|-------------------------------|
     * | prize > 0  | `room.standing.a11y`      | `room.standing.a11y.one`      |
     * | prize == 0 | `…a11y.noPrize`           | `…a11y.noPrize.one`           |
     * | bot        | `…a11y.bot`               | `…a11y.bot.one`               |
     *
     * **`.noPrize` fires on `prizeCoins === 0` regardless of `isWinner`** — the
     * same predicate the visible coin chip uses (`Room.tsx`: the chip renders at
     * `prizeCoins > 0`), so the two channels cannot drift apart about whether a
     * player won money (Ivy ruled the zero branch **omit**; there is no "won 0"
     * and no "just the glory"). Note the award group *around* the chip renders at
     * `(prizeCoins > 0) || isWinner` — a wider predicate, which is why a
     * zero-prize winner paints a crown the name does not mention. **That gap is
     * pre-existing, is one of three markers on one mechanism, and is filed, not
     * this key's** (Dex, Addendum 3).
     *
     * **The `.one` siblings carry no token** — the number is fixed in that branch,
     * so it is written into the string. `POINTS_PER_WORD = 1` makes `score === 1`
     * an ordinary Tuesday, not an edge (Nora's blocking gate, 2026-08-30).
     *
     * ── The two SLOT MODIFIERS — `SM-1` (T-642), Dex S15 Addendum 1's ruling ───
     *
     * Two new spoken facts land in this family, and Dex's key-mechanism ruling is
     * that they are **slot modifiers inside ONE named composed-name builder**, not
     * new sentence keys: *"sentence shape stays keyed, the two new facts are slot
     * modifiers, and Nora gates the enumerated composed outputs, never a
     * hand-list."* A `you × Forfeited × 4 shapes` cross-product spelled as whole
     * keys reaches **24**, which is the multiplication that ruling exists to
     * refuse.
     *
     * So each of the six shapes gains **one `{status}` slot, in one position** —
     * immediately before the score clause — and the identity modifier rides the
     * existing `{nickname}` slot:
     *
     *   `you`        → `{nickname}` receives `"Red, you"` (comma appositive, the
     *                  same grammatical slot the shipped `bot` fragment fills);
     *   `Forfeited`  → `{status}` receives `"Forfeited. "`, **empty otherwise**.
     *
     * **Why `{status}` sits before the score clause and not after the identity.**
     * It is the only position that exists in all six shapes: the `.one` siblings
     * have **no `{score}` token at all** (the number is written into the string),
     * so there is no post-identity slot they share. One slot, one position, six
     * shapes — and the twelve no-status composed outputs stay **byte-identical**
     * to what shipped, because the slot resolves to `""`.
     *
     * **The separator is a period and one space, and it is NOT a comma** — Nora's
     * 2026-09-02 REPLACE-WITH, ratified in `design/wr-s16-d5-d6-copy.md`:
     * *"`Forfeited` is a transitive verb in English, and a comma into a bare
     * numeral produces the misparse 'Red forfeited zero points … won forty-five
     * coins' — which asserts the exact inverse of the fact R-D7.2 exists to
     * carry."* Never a bare period either: *"a digit flush against a period
     * invites a decimal reading from some synthesizers."* **The period lives in
     * the builder, not in these strings**, because it is composition and not copy
     * — and that also makes R-D7.6's *"never between a money quantity and its
     * unit"* true by construction: the token terminates its clause before the
     * score clause is reached, so it can never migrate toward `won {prize}`.
     *
     * **The word itself is not minted here.** `Forfeited` is read from the same
     * derivation the visible `StatusPill` renders (`room.status.forfeited`), which
     * is Theo's *"single existing derivation of the fact"* — a token gets one
     * spelling.
     *
     * **LOCKED — winner status is never derived here.** The composed name must not
     * infer winning from `{place}`, from the score, or from row order: `Standings`
     * takes `isWinner` from the server's `summarize()`, and recomputing it
     * client-side would be a second answer to a money question.
     */
    "room.standing.a11y": "{place}: {nickname}, {status}{score} points, won {prize}.",
    /** The zero-prize shape — no `won` clause at all (D2 §1b; Ivy: omit). */
    "room.standing.a11y.noPrize": "{place}: {nickname}, {status}{score} points.",
    /** `score === 1`. No `{score}` token: the number is fixed in this branch. */
    "room.standing.a11y.one": "{place}: {nickname}, {status}1 point, won {prize}.",
    /** `score === 1` and no prize. */
    "room.standing.a11y.noPrize.one": "{place}: {nickname}, {status}1 point.",
    /**
     * **`SM-1`/T-642 — the "you" marker fragment** (Ivy `R-D7.1`, Theo's ratified
     * deck, Nora-gated 2026-09-02).
     *
     * A bare lowercase word, composed into the `{nickname}` slot as a comma
     * appositive: `"{nickname}, you"`, which mirrors the shipped `bot` fragment's
     * shape (`"{nickname}, bot, …"`) exactly.
     *
     * **Why `you` and not `your seat`**, Theo verbatim: *"The seat-roster
     * precedent (`room.seat.a11y.you`) has a bare noun 'seat' already in scope
     * because that sentence opens `"Seat {seat}:"`. The standings sentence opens
     * with an ordinal (`"{place}:"`) and has no noun to modify — forcing 'your
     * seat' in here introduces a word the sentence doesn't have."*
     *
     * **`bot × you` is refused, not a fourth identity state** — identity is a
     * single three-way state and a row is never simultaneously the viewer and a
     * bot. The builder refuses it loudly rather than composing a name for it.
     */
    "room.standing.a11y.you": "you",
    /**
     * **The ordinal words `{place}` resolves to.**
     *
     * They live here and not in a `const ORDINALS = [...]` in `apps/web` for
     * Nora's stated reason: *"an English word array inside `apps/web` is
     * player-visible text that has escaped the catalogue, the copy gate, and the
     * second locale in one move."* Numeric key segments follow `side.N.name`'s
     * precedent, and the reader is a frozen `as const` tuple so every key is a
     * literal the compiler checks — the same device `SIDES[n].nameKey` uses.
     *
     * **Capitalised, because `{place}` opens the sentence.** If this map is ever
     * reused mid-sentence that is a new copy question; do not assume a lowercase
     * variant exists (Nora).
     *
     * **The range is TEN, and it is a decision with a measured reason.** There is
     * **no seat ceiling anywhere in the system** — `game_modes.max_players` is
     * `team_count * team_size` with both only `CHECK (> 0)`, and
     * `room_configs.max_players_override` carries no CHECK at all; `@wr/config`'s
     * constraint layer caps nothing either. The largest *seeded* shape is `2v2`
     * (4 seats); the largest shape the codebase itself documents is the `10 × 1`
     * royale in `standingTeams`' own gate table. Ten covers both with headroom.
     *
     * **There is deliberately no eleventh fallback string.** Nora: *"a spoken
     * '11: Red' is the exact defect this consult closes, so '11th' or a bare digit
     * must never be reachable as a fallback."* Past ten the row composes **no
     * accessible name at all** and `placePhrase()` says so loudly — see its own
     * doc-comment for why that beats both a throw and a digit.
     */
    "room.standing.place.1": "First",
    "room.standing.place.2": "Second",
    "room.standing.place.3": "Third",
    "room.standing.place.4": "Fourth",
    "room.standing.place.5": "Fifth",
    "room.standing.place.6": "Sixth",
    "room.standing.place.7": "Seventh",
    "room.standing.place.8": "Eighth",
    "room.standing.place.9": "Ninth",
    "room.standing.place.10": "Tenth",
    /**
     * The unit a **team-mode member row's** bare score numeral is missing —
     * Ivy §22.8.3, DRAFT.
     *
     * That row deliberately carries **no `aria-label`** (one key, one shape, no
     * clone — and a label there would override its `FORFEITED` pill), so it is
     * read as its own content: nickname, pill, and a lone `90` whose meaning comes
     * from a column header the row does not repeat.
     *
     * > *"Fix: a visually-hidden ` points` after the member row's score numeral,
     * > and nothing else. No `aria-label`, no new key beyond a `room.score.unit`
     * > COPY-SLOT, no shape enumeration — it survives the pill being present or
     * > absent, and it is exactly the 'complete the sentence in place' arm of
     * > §22.8.1."*
     *
     * The word only, with no leading space: the space belongs to the markup that
     * places it, exactly as `room.seat.you`'s does one line above it in the same
     * component.
     */
    "room.score.unit": "points",
    /* ------------------------------------------------------------------------
       Bots — Theo's T-268 deck (`wr-s2-copy.md` §Bots wave), against Ivy's
       `wr-bots-product-spec.md` §10 register as narrowed by architecture A80.
  
       Two of Ivy's twelve COPY-GAPs are **cut, not deferred**:
       `room.bots.offerMoney` and `room.bots.offerMoneyRecomputed`. A80 closed
       paid-table bots structurally (LOCKED rule 32: a bot may only be seated
       where `entry_fee_snapshot = 0`), the offer card's fee line renders only at
       `entryFeeCoins > 0` (Ivy §4.3), and the two are therefore mutually
       exclusive — no fee line can ever render. Neither key exists here, and a
       future paid-bot ruling mints them fresh rather than un-commenting them.
       ------------------------------------------------------------------------ */
    /**
     * **The chip word — the primary disclosure carrier** (Ivy §3.2, LOCKED).
     *
     * Ratified verbatim from Ivy's DRAFT. ≤5 characters, and the chip is
     * `min-width` rather than a fixed width so a language with no three-letter
     * word for "bot" can grow it: §22.7's rule is that a chip may grow and may
     * **never** truncate a disclosure. **Never "AI"** — Ivy's purpose cell: that
     * word means something else to this audience and to this company.
     */
    "room.bots.marker": "BOT",
    /** The offer card's title. States the option; does not sell it (§4.3). */
    "room.bots.offerTitle": "Play against a bot instead.",
    /**
     * **The disclosure sentence.** True whether the offer is accepted, ignored or
     * times out, because it states what a bot *is* rather than what this fill is
     * doing. No difficulty language, no personality, no apology for the wait
     * (Ivy §3.6 item 2).
     */
    "room.bots.offerBody": "Bots are software, not people. The game runs the same either way.",
    /**
     * The accept button, plural form. `{n}` is the number of empty seats.
     *
     * **`t()` does flat `{n}` substitution with no ICU plural**, so a single
     * templated string is wrong at `n === 1` ("Add 1 bots") in exactly the way
     * `lobby.subtitle.live` was on 2026-07-28. That gap's fix was a second `.one`
     * key picked by the caller, and Theo reused the mechanism rather than
     * inventing a second one.
     *
     * Under this wave's scope `n` is **always 1**: bots are eligible only where
     * `entry_fee_snapshot = 0` (A80) and the only free config shipping is
     * `1v1_free` (2 seats), so this form is unreachable today. It ships anyway at
     * zero cost — if `2v2_free` ever lands, the key does not inherit a fresh
     * plural defect that has to be re-litigated.
     */
    "room.bots.offerCta": "Add {n} bots",
    /** The `n === 1` form — the only one reachable in this wave. */
    "room.bots.offerCta.one": "Add 1 bot",
    /**
     * After this player accepts with others outstanding (Ivy §4.4). Renders only
     * at `n ≥ 1`, which is the one state it can be reached in — the fill fires the
     * moment the last human accepts. "more" does not inflect, so no plural gap.
     */
    "room.bots.offerWaiting": "Waiting for {n} more to agree.",
    /**
     * The toast on a failed accept (Ivy §4.4). No blame, no error code, and the
     * button is re-enabled behind it — a bot is **never** rendered optimistically.
     */
    "room.bots.offerFailed": "That didn't go through. Try again.",
    /**
     * **The `<h2>` that replaces `room.waiting.full` when the room contains bots**
     * (Ivy §4.5, LOCKED) — the highest-value disclosure moment in the feature.
     *
     * **Reworded from Ivy's DRAFT** ("Bots have taken the empty seats") by Theo:
     * that plural noun asserts a count that is false in the only state this wave
     * can produce — `1v1_free` has exactly one empty seat and exactly one bot
     * fills it, the same "1 tables" defect Quinn caught in `lobby.left.note`.
     * *"Every X is Y"* takes a singular verb however many X's there are, so this
     * sentence is true at one bot and at three, with no `.one` variant to add if
     * `2v2_free` ever ships. It also drops production's `room.waiting.full`
     * ("Everyone's in.") — the human-implying phrasing this heading exists to
     * *not* say.
     */
    "room.bots.waitingFull": "Every open seat's a bot now.",
    /**
     * **The results-screen stats note** — Ivy §6.3 branch (B) / §10.1, Sana §8.5
     * C3+C4, Theo T-268 §8.
     *
     * ── This is the REVISED string. Theo's first pass is superseded ────────────
     *
     * His T-268 §1 row (*"Practice games against bots don't move your wins, losses
     * or streaks."*) is marked **do-not-build** in his own §8: Sana's C3 landed the
     * same day with a binding *shape* — positive clause first (what this game did
     * add to your record), then the limit as a mechanic — and it forbids naming a
     * class of game to the player. The first string opened with a negation and
     * named "practice games", which tells a player who can only reach that class
     * that they are playing the lesser product.
     *
     * So: **"Your games and words are in"** states what moved (`games_played`,
     * `words_found` — A84's transition table, checked cell by cell), and **"Wins
     * and streaks move when you play people"** states *when* the other counters
     * move rather than asserting this game failed to move them. No "don't", no
     * "count", no class named, and no apology (Ivy §3.6 item 3).
     *
     * **72 characters, and the line count is struck** — Ivy §10.1 ruled her own
     * `≤70 ch, 2 lines` budget a layout estimate wearing a constraint's clothes:
     * the real budget is *"≤80 characters and no line count at all"*. The note
     * wraps freely at every width from 180 up; three or four lines below 320 is
     * **correct**, and clipping, clamping or ellipsising it at any width is the
     * defect (§22.7.3, promoted product-wide as §22.14).
     *
     * **Renders on EVERY practice result — win, loss and draw alike (Sana C4).**
     * Disclosing only on the flattering outcome is the asymmetry this whole
     * feature exists to refuse.
     */
    "room.bots.statsNote": "Your games and words are in. Wins and streaks move when you play people.",
    /**
     * A bot's seat card (Ivy §5). Ratified verbatim.
     *
     * Matches `room.seat.a11y.you`'s shape with the bot clause at the **identity
     * marker's** position — §22.8.2's clause order (position → identity → marker →
     * state) unchanged, because a bot clause is the same kind of clause in the
     * same visual position as the viewer marker. The visible `BOT` chip on this
     * row is `aria-hidden`: the row carries an `aria-label`, which overrides its
     * subtree, so un-hiding the chip would say the word twice.
     */
    "room.seat.a11y.bot": "Seat {seat}: {nickname}, bot, {status}.",
    /**
     * A bot's 1v1 standings row (Ivy §5).
     *
     * **Diverges from Ivy's DRAFT wording, not from her intent** (Theo): it
     * matches the human key's clause shape and inserts `bot` at the
     * identity-marker position, the same rule as the seat key.
     *
     * **SUPERSEDED SHAPE (D2, 2026-08-30 — T-568).** This key used to inherit the
     * human key's `won {prize}` tail. It is now dropped **unconditionally**, for
     * the reason its own previous comment already gave: a bot only ever sits at a
     * free config under LOCKED rule 32 (A80), so `prizeCoins` is always `0` on a
     * bot row and the clause could never say anything. There is no
     * `.bot.noPrize` because there is no "bot with a prize" shape to build.
     * **Anything still citing T-268's `wr-s2-copy.md` string for this key is
     * stale; cite `wr-s14-d2-standings-name-recut.md`.**
     *
     * `{place}` is the ordinal word here too — bot rows are not exempt from D2
     * §1c. See `room.standing.a11y` above for the six-key table.
     */
    "room.standing.a11y.bot": "{place}: {nickname}, bot, {status}{score} points.",
    /** `score === 1` on a bot row. No `{score}` token; no prize clause either. */
    "room.standing.a11y.bot.one": "{place}: {nickname}, bot, {status}1 point.",
    // ---- generic errors -----------------------------------------------------
    "error.network": "Network request failed.",
    "error.unexpected": "Something went wrong.",
    "error.missingApiUrl": "VITE_API_URL is not configured for this build. The client has no API to talk to.",
    "error.missingPublicAddress": "The seat reservation carried no publicAddress, so there is no process to connect to.",
    // ---- global shared labels ------------------------------------------------
    "common.working": "Working…",
    "common.cancel": "Cancel",
    "common.backToLogin": "Back to login",
    "common.close": "Close",
    "common.dismiss": "Dismiss",
    "common.refresh": "Refresh",
    /**
     * The shared generic. Deliberately the same word as `net.retry` /
     * `splash.retry` rather than a fourth way to say it; Theo's note says the
     * screen-specific ones can migrate here later, and that it is not his call to
     * force now.
     */
    "common.retry": "Retry",
    /* ========================================================================
       In-game — the wrapper, the board and its adjacent surfaces
       (Ivy `wr-s5-game-spec.md` §12, S5-4 / S5-5 / S5-6)
       ======================================================================== */
    /**
     * ── COPY-GAP, 21 of Ivy's 22: shipped as her §12 DRAFTs, verbatim ─────────
     *
     * (The 22nd, `room.cta.join_offline`, belongs to the **Q4** ruling in the room
     * spec — a different surface and a different ticket. Named here so its absence
     * reads as scope rather than as an omission.)
     *
     * §12 is explicit that a COPY-GAP *"ships with the DRAFT in §12 so the build is
     * never blocked; Theo ratifies before S5 closes"*. So every string in this
     * block is Ivy's DRAFT character for character, and **Theo owns replacing
     * them** — the budgets are on her table, not repeated here, because a budget
     * copied into a second place is a budget that drifts.
     *
     * **Key names are Ivy's, camelCased to this catalogue's convention** (the same
     * transform `room.round.timer_label` → `room.round.timerLabel` took in S4).
     * Theo's T-227 proposed a parallel `game.word_search.*` namespace and his own
     * ticket says hers win on conflict; his family-1 rules blurb
     * (`rules.word_search.*`, ratified-final) is the one place his strings are not
     * DRAFT, and it is flagged in T-231's Output for him and Ivy to reconcile
     * against the single key §5.13 slots (`game.rules.wordSearch`).
     *
     * Three rules from §12 that the next person to edit this block must not undo:
     *
     *  - **`game.claim.you` interpolates `{points}`** and there is no literal `1`
     *    anywhere near it (§8 note 3). Scoring is +1 per word today; the param is
     *    what keeps §5.6 true if that ever changes.
     *  - **`game.claim.invalid` must say it is not one of *this round's* words.**
     *    The letters may well be a real word, and telling a 15-year-old otherwise
     *    is both rude and false.
     *  - **`game.words.unfound` and `game.reveal.note` must not read as a
     *    failure.** Nobody found them; that is a fact about the round, not about
     *    the player.
     *
     * `game.net.*` are **state names, not sentences** — the banner owns sentences,
     * and the health dot is an instrument with no live region (§4.2 LOCKED).
     */
    // ---- wrapper (§4) --------------------------------------------------------
    /** Timer eyebrow during the reveal hold. Claims nothing about the next round. */
    "game.timer.over": "ROUND OVER",
    "game.net.connected": "Connected",
    "game.net.degraded": "Reconnecting",
    "game.net.lost": "Disconnected",
    /** One parameterised score-strip cell name — never concatenated (§4.3). */
    "game.score.a11y": "{name}: {round} this round, {total} total.",
    /**
     * **A bot's in-game score cell — Ivy §6.4's sanctioned exception to §22.8.6.**
     *
     * > *"LOCKED — the `BOT` chip (compact variant) is REQUIRED in the in-game
     * > scoreboard, **and the row's accessible name must say 'bot'**. The round is
     * > the longest continuous look the player gets at their opponent; a
     * > disclosure absent there is absent when it matters most."*
     *
     * ── COPY-GAP, RATIFIED (Theo, 2026-08-04) ─────────────────────────────────
     *
     * **RATIFIED unchanged** — `design/wr-s2-copy.md` stragglers addendum §3.
     * Checked by Theo against §22.8.2's one-key-per-shape rule (a bot marker
     * changes the clause set, so it earns its own key rather than a parameter on
     * `game.score.a11y`) and against Ivy §6.4's LOCK (the row's accessible name
     * must say "bot"). The doubled "bot" in `bot_golem_94, bot: …` is a known,
     * already-ratified artifact of `room.seat.a11y.bot`: the self-disclosing name
     * and the required marker clause are two independent carriers (§3.3) and
     * neither is dropped to make the other read smoothly. Theo re-flagged the
     * missing register row below so it is not lost a second time; it is Ivy's,
     * and it does not gate this key.
     *
     * §6.4 requires this string and **Ivy's own §10 register does not carry a slot
     * for it** — the register lists only the two §5 keys (`room.seat.a11y.bot`,
     * `room.standing.a11y.bot`), and Theo's T-268 deck ratified exactly those two.
     * The cell's chip cannot announce itself the way the standings member row's
     * does, because this cell carries an `aria-label` and a label overrides its
     * subtree (§22.8.1: never both tools on one row). So the requirement resolves
     * to a key, and building nothing here would leave a LOCKED disclosure
     * unspoken.
     *
     * Written to the shipped key's exact clause shape with `bot` at the
     * identity-marker position — the same rule Theo applied to both ratified bot
     * keys, so it is a mechanical extension rather than new voice. That reasoning
     * is what he ratified above; the register row remains Ivy's.
     */
    "game.score.a11y.bot": "{name}, bot: {round} this round, {total} total.",
    /**
     * **The lead clause — S13 Q-2.** Deck-verbatim from
     * `wr-s13-d3-lead-a11y-deck.md`, section *"Re-cut per D2 reconciliation"*,
     * **Nora re-gate APPROVED 2026-08-27**. Those bytes and no others; the two
     * base keys above are unchanged and now mean *"no lead is computable for this
     * cell"* — the 3–4-side, FFA and royale case, where `scoreLead`'s two gates
     * fail and the strip states nothing at all.
     *
     * **Six whole keys, no composition** (Dex R.2, Ivy's confirming verdict): the
     * caller never concatenates a clause onto a resolved name, so the *whole
     * rendered name* is what the public-text gate reviewed — T-302 NB1 is
     * discharged by construction rather than by review discipline. Same
     * one-key-per-shape rule the `.bot` variant above is the precedent for.
     *
     * **The referent term is the base sentence's own word for the quantity
     * (`total`) — changing one changes both.** The word appears twice in each
     * string and the two occurrences must move together: re-word `{total} total.`
     * above and these six silently start naming a quantity the sentence no longer
     * uses, which is the second-inference failure Ivy's name-the-referent
     * addition exists to prevent, arriving later by drift instead of by design.
     * Fenced in `i18n.test.ts` (§Q-2) by **deriving** the referent from
     * `game.score.a11y` rather than by hard-coding `on total`.
     *
     * **Translator note:** `on total` is an English preposition idiom, not a
     * template. A translator must be free to place the referent wherever their
     * language puts it, including inside the lead word's own phrase — six whole
     * keys already give that freedom. Nobody may "simplify" these back into a
     * shared suffix or a `{lead}` parameter on the base key.
     *
     * No `.one` siblings: a lead state expresses no quantity.
     */
    "game.score.a11y.leading": "{name}: {round} this round, {total} total. Leading on total.",
    "game.score.a11y.trailing": "{name}: {round} this round, {total} total. Trailing on total.",
    "game.score.a11y.tied": "{name}: {round} this round, {total} total. Tied on total.",
    "game.score.a11y.bot.leading": "{name}, bot: {round} this round, {total} total. Leading on total.",
    "game.score.a11y.bot.trailing": "{name}, bot: {round} this round, {total} total. Trailing on total.",
    "game.score.a11y.bot.tied": "{name}, bot: {round} this round, {total} total. Tied on total.",
    // ---- board (§5) ----------------------------------------------------------
    "game.words.eyebrow": "WORDS",
    "game.words.progress": "{found}/{total}",
    /** Suffix on a revealed unfound chip. **Must not read as a failure** (§5.8). */
    "game.words.unfound": "NOT FOUND",
    "game.words.chipA11y": "{word}, claimed by {nickname}.",
    /** **`{points}` is a param, never a literal 1** (§8 note 3, LOCKED). */
    "game.claim.you": "{word} — +{points}",
    /** No blame, no "too slow", no exclamation (§12). */
    "game.claim.taken": "{nickname} got there first.",
    /** *This round's* words — the letters may be a perfectly real word (§12). */
    "game.claim.invalid": "Not one of the words.",
    /** During the reveal hold. Neutral, not disappointed (§5.9). */
    "game.reveal.note": "Nobody found these.",
    "game.a11y.grid": "Word search grid, {n} by {n}. Use the arrow keys to move and Enter to start a selection.",
    "game.a11y.cell": "{letter}, row {row}, column {col}.",
    "game.a11y.cellClaimed": "{letter}, row {row}, column {col}, claimed by {nickname}.",
    /** A single letter is the anchor case of the same key (§7 item 5). */
    "game.a11y.selection": "Selecting {letters}",
    // ---- adjacent surfaces (§5.10, §5.12, §5.13) -----------------------------
    /** **Must not blame the player and must not claim anything about their entry.** */
    "game.loadError.body": "We can't load the game right now.",
    "game.unknown.title": "Time for an update.",
    /**
     * §5.12 LOCKED: it says the app needs updating, **does not offer a reload as a
     * fix** (a reload serves the same bundle), and **makes no claim about the
     * entry fee**. What happened to the money is the room's business and the
     * player reads it at `results` like everyone else.
     */
    "game.unknown.body": "This game needs a newer version of the app.",
    /**
     * §5.13 — renders in W only, on a plate under the seat area. Two sentences.
     *
     * **Ratified by Theo (T-235 §B): Ivy's DRAFT is replaced by his
     * `rules.word_search.short`**, and the key stays hers. Three reasons, his:
     * the ratified line went through mechanics review against S5-2/S5-3 and PRD
     * §11.1 while the DRAFT was a placeholder; it is 61 characters against a 180
     * budget; and *"drag across the letters"* names one of the **three** input
     * paths §5.5 ships (drag, tap-tap, keyboard), so it is mildly wrong for a
     * keyboard or switch player where "find and claim" is right for all three.
     *
     * His standalone `rules.word_search.short` key is retired rather than cloned —
     * one string, one key. `rules.word_search.full.*` stays ratified-final and
     * **unwired**: no help or rules surface exists (§5.13 LOCKED bars any modal
     * over a live board), so wiring it would be inventing the surface.
     */
    "game.rules.wordSearch": "Same grid, same words. First to find and claim one gets it.",
    /**
     * **Word Reveal's rules line — SHIPPED UNWIRED, by Ivy's instruction** (S14,
     * her ruling on T-575's STOP; the wiring is S15's `RULES-BLURB-ENGINE-KEY`
     * row, activation-gated).
     *
     * > *"Ship the deck's `game.rules.hangman` string now, unwired — it renders
     * > nowhere, costs nothing, and makes the S15 row pure wiring with no copy
     * > gate attached."*
     *
     * **Nothing reads this key today.** `RoomScreen`'s §5.13 blurb is hard-coded
     * to `game.rules.wordSearch`, and changing that is a shared-chrome edit that
     * H3 was barred from and S15 owns. The consequence, recorded so it is not
     * rediscovered: until that row lands, a Hangman room's waiting screen would
     * say *"Same grid, same words…"* — which is why activation is gated behind it.
     *
     * Nora's §7 REPLACE-WITH, verbatim. Two changes from Theo's draft and both
     * are rules, not taste: the noun is **power** (R-B4 — nothing may imply the
     * meter touches money), and *"Right guesses"* became **"Right letters"**
     * because **R-A3** bars any copy implying a whole-word solve exists, and
     * *"right guesses reveal it"* is the one line where a reader could picture
     * typing a word.
     */
    "game.rules.hangman": "Same word for everyone. Right letters reveal it; wrong ones drain your power.",
    /* ------------------------------------------------------------------------
       Word Reveal — the second game type's board (S14 `H3`).
  
       Every string below is byte-verbatim from Theo's D5 deck AS GATED BY NORA
       (`wr-s14-d5-hangman-strings.md`, gate dated 2026-08-30), including all four
       of her REPLACE-WITHs. **The meter's noun is `Power`, not `Charge`** — her
       one substantive call, on the ground that *"a charge is a fee"* in a product
       with entry fees and a coins ledger, and R-B4 LOCKS that nothing on this
       board may imply the meter touches money.
  
       **The key segment is the engine key `hangman`, never the public name.** That
       is the catalogue's existing pattern (`lobby.type.word_search.tagline`,
       `stats.label.word_search.*`) and it is what let Aaron's naming decision land
       without touching a key.
  
       **No `.one` sibling exists anywhere in this family, and that is structural**
       (deck note 1): after Nora's replacements no string here contains a noun that
       pluralises on a variable. `meter.a11y` counts nothing ("4 of 6 left"), and
       `word.a11y`'s "letters" attaches to `{length}`, which the params schema
       floors at 4. **If you find yourself minting `.one` for one of these, a
       string has drifted — go back to the deck.** Reopen trigger, in Nora's own
       re-phrasing: *if the schema's minimum ever admits a one-letter word.*
       ------------------------------------------------------------------------ */
    /**
     * The meter's noun (R-E2, ≤8 chars). Stored Title Case; the board renders it
     * uppercase through its `mono-xs` treatment, the same mechanism
     * `lobby.money.entry` → `ENTRY` uses.
     */
    "game.hangman.meter.label": "Power",
    /**
     * **R-E5 — the meter's accessible name, one parameterised string, never
     * concatenated.** Deck note 3 binds the consumer: the visible `POWER` label is
     * `aria-hidden`, or a listener hears "Power, power meter 4 of 6 left."
     */
    "game.hangman.meter.a11y": "Power meter, {remaining} of {total} left.",
    /**
     * **One key, used twice** (deck note 2): the persistent line in the meter band
     * (§2.1 rule 3 — a permanent condition never gets a transient toast) **and**
     * the single `aria-live="polite"` announcement at the moment the meter empties
     * (§2.1 rule 4). Minting a `.spent.announcement` sibling is a second copy of
     * one fact.
     *
     * *"No guesses this round"* rather than *"You're done guessing"* is Nora's
     * replacement and it is a truth fix, not a style one: the scope clause is what
     * keeps a three-round room from reading as elimination, which R-B4 forbids.
     */
    "game.hangman.meter.spent": "No power left. No guesses this round.",
    /**
     * **R-F9 — built from the mask alone: counts, never letters.** The mid-round
     * DOM is an attack surface and must contain the mask and nothing else.
     */
    "game.hangman.word.a11y": "Word, {revealed} of {length} letters revealed.",
    /**
     * The viewer's own credited guess, on the **authoritative patch only**.
     * `{points}` is the server's whole number for that guess (letter value ×
     * occurrences, plus the solve bonus when it lands) — **never a literal**, the
     * same discipline `game.claim.you` carries. The dash is U+2014, byte-identical
     * to that key's (deck note 4).
     */
    "game.hangman.guess.hit": "{letter} — +{points}",
    /** The viewer's own authoritative miss. States the fact the miss made public. */
    "game.hangman.guess.miss": "Not in the word.",
    /**
     * Lost the race for a letter. **Verbatim reuse of `game.claim.taken`** — one
     * sentence, two keys, on the catalogue's own `stats.label.best_game_score`
     * twins precedent, because the fact is identical in both games.
     */
    "game.hangman.guess.taken": "{nickname} got there first.",
    /**
     * During the round-end hold (R-C4). *"The rest"* rather than *"these"* is
     * Nora's replacement: the hold runs whenever **at least one** letter was still
     * hidden, so a plural demonstrative pointing at a single dashed slot is a
     * reachable render — the "1 tables" bug in another coat.
     */
    "game.hangman.reveal.note": "Nobody guessed the rest.",
    /* ========================================================================
       The shop storefront — S8 E7 `SHOP-STOREFRONT-WEB` (T-404)
  
       Every string below is byte-verbatim from Theo's FINAL, Nora-cleared deck:
       `design/wr-shop-copy-deck.md` (T-380, 2026-08-13). `shop.confirm.cancel`,
       `shop.confirm.working` and `shop.refuse.close` are deliberately ABSENT —
       the deck's own §4 rules them reuses of `common.cancel`, `common.working`
       and `common.close` above, and minting a duplicate key here would be a
       second copy of a fact (A81) the deck explicitly warns against.
  
       **LOCKED coupling (Ivy, 2026-08-13 addendum): `shop.closed.title` and
       `shop.price.a11y` share one second sentence, word for word — edit one,
       edit both, or the echo the addendum calls "deliberate and load-bearing"
       breaks silently.**
       ======================================================================== */
    "shop.title": "Shop",
    "shop.category.avatar": "Avatars",
    "shop.category.frame": "Frames",
    // Deliberately singular — "Flairs" isn't a word a 15-year-old uses. Deck §9,
    // Nora S9: a considered deviation from Ivy's "plural section heading" rule,
    // recorded rather than silently different.
    "shop.category.flair": "Flair",
    "shop.category.emote": "Emotes",
    "shop.preview.notice": "Everything here is real. Nothing's for sale yet.",
    "shop.closed.title": "The shop isn't open yet",
    "shop.closed.body": "This is where avatars, frames and other profile looks will live.",
    "shop.closed.cta": "Back to lobby",
    /**
     * **NORA-CLEARED, ships exactly as written — do not shorten.** "yet" is
     * load-bearing (deck §3): it names an unfinished process with a doer (the
     * shop), where "Not priced" alone reads as a defect of the item. 14/14 —
     * the tightest string in the deck; there is no headroom to trim.
     */
    "shop.price.unset": "Not priced yet",
    /** Coupled with `shop.closed.title` — see the section banner above. */
    "shop.price.a11y": "Not priced. The shop isn't open yet.",
    "shop.card.owned": "Owned",
    "shop.card.a11y": "{name}, {category}. {priceState} {ownedState}",
    "shop.confirm.cta": "Get it",
    "shop.inventory.unknown": "We can't tell what you already own, so we're not offering this. Try again.",
    "shop.error.network": "That didn't reach the server. Check your connection and try again.",
    // ---- the refusal union, all seven codes (deck §5, all written now) -------
    "shop.refuse.unavailable": "The shop isn't open yet. Come back once it is.",
    "shop.refuse.notFound": "This item's gone. See what else is here.",
    "shop.refuse.notLive": "This item isn't for sale right now. Take a look at something else.",
    "shop.refuse.alreadyOwned": "You already have this one.",
    "shop.refuse.maxReached": "You've hit the limit on this one.",
    "shop.refuse.insufficient": "You need {n} more coins for this.",
    "shop.refuse.unknown": "That didn't go through. Close this and try again.",
    /**
     * **The eighth code** (deck §10.2, Nora-fixed 2026-08-17, applied verbatim).
     * The union's own count claim in deck §5 said "seven" and was superseded by
     * §10's addendum — `price_changed` joined it at A121 addendum-2.
     *
     * **Fact, then reassurance — deliberately not fact-then-direction.** "Take
     * another look" was Nora's blocking finding: §6.1 makes `priceCoins` a
     * snapshot with no poll, and §5.4 ends the flow on a typed refusal, so
     * re-opening the dialog would show the same stale price and refuse
     * identically. The sentence a money refusal actually has to answer is *did
     * that just take my coins?*
     */
    "shop.refuse.priceChanged": "This item's price changed. You haven't spent anything.",
    /* ---- the purchase succeeded (deck §10.3.1, Ivy's equip ruling §4.4/§4.5) --
       Three strings, and the third has TWO call sites by design: the success
       state's primary action and an already-owned item's dialog primary are the
       same "wear it" navigation to `/profile?edit=avatar` (A124 amendment 3 —
       one string, two entries, so they cannot drift).
  
       **No coin amount here, and that is Ivy's constraint, not an oversight:**
       the top-bar chip animates the debit and is the one truth for the balance
       (S3 §9.1). A second number in the dialog is a second copy of a money fact. */
    "shop.success.title": "It's yours",
    "shop.success.body": "You can wear it from your profile.",
    "shop.success.wear": "Wear it",
    "shop.empty.title": "Nothing here right now",
    "shop.empty.body": "The shop's open. There's just nothing in it.",
    "shop.error.body": "Couldn't load the shop. Check your connection and try again.",
    "shop.loading": "Loading the shop…",
    // ---- not found ----------------------------------------------------------
    "notFound.title": "Nothing here",
    "notFound.body": "That route does not exist.",
    "notFound.home": "Back to the start",
};
//# sourceMappingURL=en.js.map